import qs from 'qs';
import QueryString from 'qs';
import { v4 as uuidv4 } from 'uuid';

import { components, operations } from '@luxuryescapes/contract-svc-promo/generated';

import {
  GetPromoDisplayCheckResultsFilter,
  PromoDisplayCheckResultsResponse,
} from '~/hooks/usePromoDisplayCheckResults';

import ApiError from '~/types/ApiError';

import { normalizeToISODate } from '~/utils/dateFormatter';
import { filterOutEmptyFields } from '~/utils/objectUtils';
import { fetchAndSanitizeSchema } from '~/utils/rjsfValidator';

import { dateNow, formatDateDateTimeWidget } from './TimeService';
import { request } from './common';

type definitions = components['schemas'];
type PromoSearchQuery = operations['searchPromos']['parameters']['query'];

export type PromoCategory = NonNullable<PromoSearchQuery['promo_category']>;

export const promoCategoryOptions: ReadonlyArray<{ label: string; value: PromoCategory }> = [
  { label: 'Regular', value: 'regular' },
  { label: 'Friends and Family', value: 'friends_and_family' },
];

export function isPromoCategory(value: string | null): value is PromoCategory {
  return value === 'regular' || value === 'friends_and_family';
}

/** Attach `result.errors` (promo clone/preview) to thrown `ApiError` without changing shared `request()` behaviour. */
function throwVoucherCloneErrorIfPresent(body: unknown): void {
  if (typeof body !== 'object' || body === null) {
    return;
  }
  const b = body as {
    status?: number;
    message?: string;
    result?: { errors?: Array<unknown> };
    errors?: Array<unknown>;
  };
  const fromResult = b.result?.errors;
  const list = Array.isArray(fromResult) ? fromResult : Array.isArray(b.errors) ? b.errors : null;
  if (!list?.length) {
    return;
  }
  const httpStatus = typeof b.status === 'number' ? b.status : undefined;
  throw new ApiError({
    name: 'NetworkError',
    message: `Network error received (status code ${httpStatus ?? 'unknown'}): ${b.message ?? 'Unknown error'}`,
    status: httpStatus,
    errors: list,
  });
}

function basePath() {
  return window.configs.API_HOST + '/api/';
}

/** Promo code name pattern (aligned with svc-promo validation). */
export const promoCodeValidationRegex = /^[A-Za-z0-9_-]+$/;

// Vouchers
export type ListVouchersQuery = operations['listVouchers']['parameters']['query'];
export type ListVouchersResponse = operations['listVouchers']['responses']['200']['content']['application/json'];
export type CreateVoucherPayload = operations['createVoucher']['requestBody']['content']['application/json'];
export type CreateVoucherResponse = operations['createVoucher']['responses']['201']['content']['application/json'];
export type RequestRestrictedPromoPayload =
  operations['requestRestrictedPromo']['requestBody']['content']['application/json'];
export type RequestRestrictedPromoResponse =
  operations['requestRestrictedPromo']['responses']['200']['content']['application/json'];
export type GetFriendsAndFamilyUsageQuery = operations['getFriendsAndFamilyUsage']['parameters']['query'];
export type GetFriendsAndFamilyUsageResponse =
  operations['getFriendsAndFamilyUsage']['responses']['200']['content']['application/json'];
export type GetVoucherResponse = operations['getVoucher']['responses']['200']['content']['application/json'];
export type UpdateVoucherPayload = operations['updateVoucher']['requestBody']['content']['application/json'];
export type UpdateVoucherResponse = operations['updateVoucher']['responses']['200']['content']['application/json'];
export type AddVoucherTogglePayload = operations['addVoucherToggle']['requestBody']['content']['application/json'];
export type AddVoucherToggleResponse =
  operations['addVoucherToggle']['responses']['200']['content']['application/json'];
export type SearchPromosByToggleQuery = operations['searchPromosByToggle']['parameters']['query'];
export type SearchPromosByToggleResponse =
  operations['searchPromosByToggle']['responses']['200']['content']['application/json'];
export type DeleteVoucherResponse = operations['deleteVoucher']['responses']['200']['content']['application/json'];
export type CheckVoucherCodeQuery = operations['checkVoucherCode']['parameters']['query'];
export type CheckVoucherCodeResponse =
  operations['checkVoucherCode']['responses']['200']['content']['application/json'];
export type ListVoucherUsersQuery = operations['listVoucherUsers']['parameters']['query'];
export type ListVoucherUsersResponse =
  operations['listVoucherUsers']['responses']['200']['content']['application/json'];
export type ListVoucherLogsQuery = operations['listVoucherLogs']['parameters']['query'];
export type ListVoucherLogsResponse = operations['listVoucherLogs']['responses']['200']['content']['application/json'];
export type ListVoucherLogsResult = ListVoucherLogsResponse['result'];
export type VoucherLogWarning = components['schemas']['VoucherLogWarning'];
export type VoucherLogMetadata = components['schemas']['VoucherLogMetadata'] & {
  /** Compatibility fields observed on some voucher-log payloads. */
  voucherRewardKind?: string;
  rewardKind?: string;
  voucher_reward_kind?: string;
};
export type VoucherLog = ListVoucherLogsResponse['result']['logs'][number] & {
  metadata?: VoucherLogMetadata;
  userId?: string;
  /** Present on some list-log payloads when not folded into `metadata`. */
  rewardKind?: string;
  reward_kind?: string;
  fk_order?: string | number;
  /** Present on list-log rows; may duplicate `metadata.creditGrantedAt`. */
  creditGrantedAt?: string;
};
export type CreateVoucherUsersPayload =
  operations['createVoucherUsers']['requestBody']['content']['application/json'] & {
    brand?: definitions['Promo Brands'];
  };
export type CreateVoucherUsersResponse =
  operations['createVoucherUsers']['responses']['201']['content']['application/json'];
export type ExpireVoucherUserResponse =
  operations['expireVoucherUser']['responses']['200']['content']['application/json'];
export type ListAllVoucherUsersQuery = operations['listAllVoucherUsers']['parameters']['query'];
export type ListAllVoucherUsersResponse =
  operations['listAllVoucherUsers']['responses']['200']['content']['application/json'];
export type UnlogVoucherPayload = operations['unlogVoucher']['requestBody']['content']['application/json'];
export type UnlogVoucherResponse = operations['unlogVoucher']['responses']['200']['content']['application/json'];
export type Voucher =
  operations['listVouchers']['responses']['200']['content']['application/json']['result']['vouchers'][number];
export type VoucherToggle = Voucher['voucherToggles'][number];

type ContractVoucherWarning = NonNullable<Voucher['voucherWarnings']>[number];
export type VoucherWarning = ContractVoucherWarning;

/**
 * Reads `voucherWarnings` from a voucher list/detail payload.
 *
 * The promo contract types use camelCase (`voucherWarnings`). In practice the HTTP JSON body can still
 * expose `voucher_warnings` if it is not normalized before it reaches app code. Prefer fixing that in
 * `request()` or a response mapper; this helper keeps UI and tables tolerant until then.
 */
export function getVoucherWarnings(voucher: Voucher): Array<VoucherWarning> {
  const withSnake = voucher as Voucher & { voucher_warnings?: Array<VoucherWarning> };
  const list = voucher.voucherWarnings ?? withSnake.voucher_warnings;
  return Array.isArray(list) ? list : [];
}

export type VoucherUserExpiryApiPayload = {
  voucher_user_expires_at: string | null;
  voucher_user_expires_in_days: number | null;
};

function voucherUserExpiryCamelToSnakeForPatch(body: Record<string, unknown>): void {
  if (Object.prototype.hasOwnProperty.call(body, 'voucherUserExpiresInDays')) {
    const camel = body.voucherUserExpiresInDays;
    delete body.voucherUserExpiresInDays;
    if (body.voucher_user_expires_in_days === undefined) {
      if (camel === null || camel === undefined || camel === '') {
        body.voucher_user_expires_in_days = null;
      } else {
        const parsed = typeof camel === 'number' ? camel : Number.parseInt(String(camel), 10);
        body.voucher_user_expires_in_days = Number.isNaN(parsed) || parsed < 0 ? null : parsed;
      }
    }
  }

  if (Object.prototype.hasOwnProperty.call(body, 'voucherUserExpiresAt')) {
    const camel = body.voucherUserExpiresAt;
    delete body.voucherUserExpiresAt;
    if (body.voucher_user_expires_at === undefined) {
      if (camel == null || camel === '') {
        body.voucher_user_expires_at = null;
      } else {
        body.voucher_user_expires_at = normalizeToISODate(String(camel));
      }
    }
  }
}

export type BulkUpdateUnusedVoucherUsersExpiryPayload =
  operations['bulkUpdateUnusedVoucherUsersExpiry']['requestBody']['content']['application/json'];
export type BulkUpdateUnusedVoucherUsersExpiryResponse =
  operations['bulkUpdateUnusedVoucherUsersExpiry']['responses']['200']['content']['application/json'];

export type ClearVoucherUsersPayload = operations['clearVoucherUsers']['requestBody']['content']['application/json'];
export type ClearVoucherUsersResponse =
  operations['clearVoucherUsers']['responses']['200']['content']['application/json'];

export type BulkDeleteVoucherUsersPayload =
  operations['bulkDeleteVoucherUsers']['requestBody']['content']['application/json'];
export type BulkDeleteVoucherUsersResponse =
  operations['bulkDeleteVoucherUsers']['responses']['200']['content']['application/json'];

/**
 * svc-promo accepts this request metadata, but the installed generated contract has not yet
 * caught up. Responses continue to use the generated contract because the client derives
 * batch progress from its serial request queue.
 */
export type BulkDeleteVoucherUsersBatchPayload = BulkDeleteVoucherUsersPayload & {
  batchId: string;
  batchRunCountTotal: number;
  batchRunCount: number;
};

export type CloneVoucherRequestBody = operations['cloneVoucher']['requestBody']['content']['application/json'] & {
  brand?: definitions['Promo Brands'];
};
export type CloneVoucherPreviewQuery = operations['previewVoucherClone']['parameters']['query'] & {
  brand?: definitions['Promo Brands'];
};
export type CloneVoucherPreviewResponse =
  operations['previewVoucherClone']['responses']['200']['content']['application/json'];
export type CloneVoucherPreviewResult = CloneVoucherPreviewResponse['result'];
export type CloneVoucherResponse = operations['cloneVoucher']['responses']['201']['content']['application/json'];
export type CloneVoucherUserAssignment = NonNullable<CloneVoucherRequestBody['voucherUsers']>[number];
export type PreviewVoucherPayload = operations['previewVoucher']['requestBody']['content']['application/json'];
export type PreviewVoucherResponse = operations['previewVoucher']['responses']['200']['content']['application/json'];
export type PreviewPublicVoucher = PreviewVoucherResponse['result']['publicVoucher'];
export type PreviewVoucherTextReplacement = PreviewVoucherResponse['result']['textReplacements'][number];

export async function getCloneVoucherPreview(
  query: CloneVoucherPreviewQuery,
  options?: { signal?: AbortSignal },
): Promise<CloneVoucherPreviewResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/vouchers/clone?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
    errorBodyCallback: throwVoucherCloneErrorIfPresent,
  });
}

export async function cloneVoucher(
  body: CloneVoucherRequestBody,
  options?: { signal?: AbortSignal },
): Promise<CloneVoucherResponse> {
  return request(`${basePath()}promo/vouchers/clone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: options?.signal,
    errorBodyCallback: throwVoucherCloneErrorIfPresent,
  });
}

export async function previewVoucher(
  body: PreviewVoucherPayload,
  options?: { signal?: AbortSignal },
): Promise<PreviewVoucherResponse> {
  return request(`${basePath()}promo/vouchers/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: options?.signal,
    errorBodyCallback: throwVoucherCloneErrorIfPresent,
  });
}

export async function listVouchers(q: ListVouchersQuery): Promise<ListVouchersResponse> {
  const queryString = qs.stringify(q, { skipNulls: true });
  return request(`${basePath()}promo/vouchers?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export const defaultExpiryDays = 180;

export async function createVoucher(payload: CreateVoucherPayload): Promise<CreateVoucherResponse> {
  return request(`${basePath()}promo/vouchers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function getVoucher(voucherId: string): Promise<GetVoucherResponse> {
  return request(`${basePath()}promo/vouchers/${voucherId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

/** GET /api/promo/voucher/check — validates voucher code and returns presentation strings (and optional usage counts). */
export async function checkVoucherCode(
  query: CheckVoucherCodeQuery,
  options?: { signal?: AbortSignal },
): Promise<CheckVoucherCodeResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/voucher/check?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function updateVoucher(
  voucherId: string,
  payload: UpdateVoucherPayload & Partial<VoucherUserExpiryApiPayload>,
): Promise<UpdateVoucherResponse> {
  const body: Record<string, unknown> = { ...payload };
  voucherUserExpiryCamelToSnakeForPatch(body);
  return request(`${basePath()}promo/vouchers/${voucherId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Adds one voucher toggle without replacing the voucher's existing toggle set. */
export async function addVoucherToggle(payload: AddVoucherTogglePayload): Promise<AddVoucherToggleResponse> {
  return request(`${basePath()}promo/vouchers/add-toggle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function searchPromosByToggle(
  query: SearchPromosByToggleQuery,
  options?: { signal?: AbortSignal },
): Promise<SearchPromosByToggleResponse> {
  return request(`${basePath()}promo/by-toggle?${qs.stringify(query)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function deleteVoucher(voucherId: string): Promise<DeleteVoucherResponse> {
  return request(`${basePath()}promo/vouchers/${voucherId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export type SyncVoucherLabAudienceResponse = {
  status: number;
  message: string;
  result: { labAudienceId: number; membersSynced: number; chunksSent: number };
};

export async function syncVoucherLabAudience(
  voucherId: string,
  labAudienceId: number,
): Promise<SyncVoucherLabAudienceResponse> {
  return request(`${basePath()}promo/vouchers/${voucherId}/sync-lab-audience`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ labAudienceId }),
  });
}

export type ListVoucherAutoAssignmentRulesQuery = operations['listVoucherAutoAssignmentRules']['parameters']['query'];
export type ListVoucherAutoAssignmentRulesResponse =
  operations['listVoucherAutoAssignmentRules']['responses']['200']['content']['application/json'];
export type ListVoucherAutoAssignmentRuleAssignmentGroupsQuery =
  operations['listVoucherAutoAssignmentRuleAssignmentGroups']['parameters']['query'];
export type ListVoucherAutoAssignmentRuleAssignmentGroupsResponse =
  operations['listVoucherAutoAssignmentRuleAssignmentGroups']['responses']['200']['content']['application/json'];
export type CreateVoucherAutoAssignmentRulePayload =
  operations['createVoucherAutoAssignmentRule']['requestBody']['content']['application/json'];
export type CreateVoucherAutoAssignmentRuleResponse =
  operations['createVoucherAutoAssignmentRule']['responses']['201']['content']['application/json'];
export type UpdateVoucherAutoAssignmentRulePayload =
  operations['updateVoucherAutoAssignmentRule']['requestBody']['content']['application/json'];
export type UpdateVoucherAutoAssignmentRuleResponse =
  operations['updateVoucherAutoAssignmentRule']['responses']['200']['content']['application/json'];
export type DeleteVoucherAutoAssignmentRuleResponse =
  operations['deleteVoucherAutoAssignmentRule']['responses']['200']['content']['application/json'];
export type VoucherAutoAssignmentRule = ListVoucherAutoAssignmentRulesResponse['result']['rules'][number];
export type GetVoucherAutoAssignmentRuleResponse =
  operations['getVoucherAutoAssignmentRule']['responses']['200']['content']['application/json'];
export type VoucherAutoAssignmentRuleFetchResult = GetVoucherAutoAssignmentRuleResponse['result'];
export type GetVoucherOrderAutoAssignResponse =
  operations['getVoucherOrderAutoAssign']['responses']['200']['content']['application/json'];
export type ManualVoucherAutoAssignPayload =
  operations['manualVoucherAutoAssign']['requestBody']['content']['application/json'];
export type ManualVoucherAutoAssignResponse =
  operations['manualVoucherAutoAssign']['responses']['200']['content']['application/json'];
export type VoucherOrderAutoAssignRuleOutcome = GetVoucherOrderAutoAssignResponse['result']['outcomes'][number];
export type BulkVoucherOrderAutoAssignByOrderFiltersPayload =
  operations['bulkVoucherOrderAutoAssignByOrderFilters']['requestBody']['content']['application/json'];
export type BulkVoucherOrderAutoAssignByOrderFiltersResponse =
  operations['bulkVoucherOrderAutoAssignByOrderFilters']['responses']['200']['content']['application/json'];

/**
 * Assigned to the voucher-user when it is created as a result of an order-complete voucher auto-assign rule.
 */
export const VOUCHER_USER_REPORTING_TAG_AUTO_ASSIGN_ORDER = 'auto-assign-order';

/** Read-only evaluation of voucher auto-assign rules for an order (dry-run). */
export async function getVoucherOrderAutoAssign(
  orderId: string,
  options?: { signal?: AbortSignal },
): Promise<GetVoucherOrderAutoAssignResponse> {
  const queryString = qs.stringify({ order_id: orderId.trim() }, { skipNulls: true });
  return request(`${basePath()}promo/vouchers/order-auto-assign?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

/** Manually run voucher auto-assign for an order (persists assignments when rules match). */
export async function manualVoucherAutoAssign(
  payload: ManualVoucherAutoAssignPayload,
  options?: { signal?: AbortSignal },
): Promise<ManualVoucherAutoAssignResponse> {
  return request(`${basePath()}promo/vouchers/order-auto-assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

/** Bulk search orders by filters, then evaluate or apply voucher auto-assignment for each order. */
export async function bulkVoucherOrderAutoAssignByOrderFilters(
  payload: BulkVoucherOrderAutoAssignByOrderFiltersPayload,
  options?: { signal?: AbortSignal },
): Promise<BulkVoucherOrderAutoAssignByOrderFiltersResponse> {
  return request(`${basePath()}promo/vouchers/order-auto-assign/by-order-filters`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function listVoucherAutoAssignmentRules(
  query: ListVoucherAutoAssignmentRulesQuery,
  options?: { signal?: AbortSignal },
): Promise<ListVoucherAutoAssignmentRulesResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/vouchers/auto-assignment-rules?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function listVoucherAutoAssignmentRuleAssignmentGroups(
  query: ListVoucherAutoAssignmentRuleAssignmentGroupsQuery = {},
  options?: { signal?: AbortSignal },
): Promise<ListVoucherAutoAssignmentRuleAssignmentGroupsResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/vouchers/auto-assignment-rules/assignment-groups?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function createVoucherAutoAssignmentRule(
  payload: CreateVoucherAutoAssignmentRulePayload,
  options?: { signal?: AbortSignal },
): Promise<CreateVoucherAutoAssignmentRuleResponse> {
  return request(`${basePath()}promo/vouchers/auto-assignment-rules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function updateVoucherAutoAssignmentRule(
  voucherAutoAssignmentRuleId: string,
  payload: UpdateVoucherAutoAssignmentRulePayload,
  options?: { signal?: AbortSignal },
): Promise<UpdateVoucherAutoAssignmentRuleResponse> {
  return request(`${basePath()}promo/vouchers/auto-assignment-rules/${voucherAutoAssignmentRuleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function getVoucherAutoAssignmentRule(
  voucherAutoAssignmentRuleId: string,
  options?: { signal?: AbortSignal },
): Promise<GetVoucherAutoAssignmentRuleResponse> {
  return request(`${basePath()}promo/vouchers/auto-assignment-rules/${voucherAutoAssignmentRuleId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function deleteVoucherAutoAssignmentRule(
  voucherAutoAssignmentRuleId: string,
  options?: { signal?: AbortSignal },
): Promise<DeleteVoucherAutoAssignmentRuleResponse> {
  return request(`${basePath()}promo/vouchers/auto-assignment-rules/${voucherAutoAssignmentRuleId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function listVoucherUsers(
  voucherId: string,
  query: ListVoucherUsersQuery,
): Promise<ListVoucherUsersResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/vouchers/${voucherId}/users?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function listVoucherLogs(query: ListVoucherLogsQuery): Promise<ListVoucherLogsResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  if (!query.brand) {
    console.warn('listVoucherLogs:listVoucherLogs should always include the brand (defaulting to luxuryescapes)');
    query.brand = 'luxuryescapes';
  }
  return request(`${basePath()}promo/vouchers/logs?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function createVoucherUsers(payload: CreateVoucherUsersPayload): Promise<CreateVoucherUsersResponse> {
  return request(`${basePath()}promo/voucher-users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function expireVoucherUser(voucherUserId: string): Promise<ExpireVoucherUserResponse> {
  return request(`${basePath()}promo/voucher-users/${voucherUserId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function bulkUpdateUnusedVoucherUsersExpiry(
  payload: BulkUpdateUnusedVoucherUsersExpiryPayload,
  options?: { signal?: AbortSignal },
): Promise<BulkUpdateUnusedVoucherUsersExpiryResponse> {
  return request(`${basePath()}promo/voucher-users/bulk-update-expiry`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function clearVoucherUsers(
  payload: ClearVoucherUsersPayload,
  options?: { signal?: AbortSignal },
): Promise<ClearVoucherUsersResponse> {
  return request(`${basePath()}promo/voucher-users/clear-voucher`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function bulkDeleteVoucherUsers(
  payload: BulkDeleteVoucherUsersPayload,
  options?: { signal?: AbortSignal },
): Promise<BulkDeleteVoucherUsersResponse> {
  return request(`${basePath()}promo/voucher-users/bulk-delete`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function bulkDeleteVoucherUsersBatched(
  payload: BulkDeleteVoucherUsersBatchPayload,
  options?: { signal?: AbortSignal },
): Promise<BulkDeleteVoucherUsersResponse> {
  return request(`${basePath()}promo/voucher-users/bulk-delete`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function unlogVoucher(payload: UnlogVoucherPayload): Promise<UnlogVoucherResponse> {
  return request(`${basePath()}promo/vouchers/log`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/** Body for `POST /api/promo/vouchers/credit-grants/process-order` (svc-promo). */
export type ProcessCreditGrantOrderPayload = {
  orderIds: Array<string>;
  /** When true, the service returns a preview without persisting changes. */
  dryRun?: boolean;
};
export type ProcessCreditGrantOrderResponse =
  operations['processVoucherCreditGrantForOrder']['responses']['200']['content']['application/json'];

export async function processCreditGrantOrder(
  payload: ProcessCreditGrantOrderPayload,
  options?: { signal?: AbortSignal },
): Promise<ProcessCreditGrantOrderResponse> {
  return request(`${basePath()}promo/vouchers/credit-grants/process-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function listAllVoucherUsers(
  query: ListAllVoucherUsersQuery,
  options?: { signal?: AbortSignal },
): Promise<ListAllVoucherUsersResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/voucher-users?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export type ListAudiencesQuery = {
  page?: number;
  limit?: number;
  search?: string;
};

export type SearchAudienceAdminListQuery = {
  query?: string;
  brand?: string;
  region?: string;
  limit?: number;
};

export type GetAudiencePublicListQuery = {
  brand?: string;
  region?: string;
  sessionId?: string;
  userId?: string;
  audienceId?: string;
  userType?: 'lux-plus-members' | 'non-lux-plus-members';
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
};

export type ListAudiencesResponse = {
  status: number;
  message?: string;
  result: {
    audiences: Array<components['schemas']['AudienceSearchOutput']>;
    total?: number;
  };
};

export type ListAudienceUsersQuery = operations['getAudienceUsersAdminList']['parameters']['query'];

export type GetAudienceUsersAdminListQuery = operations['getAudienceUsersAdminList']['parameters']['query'];
export type GetAudienceUsersAdminListResponse =
  operations['getAudienceUsersAdminList']['responses']['200']['content']['application/json'];
export type AudienceUserAdminOutput = components['schemas']['AudienceUserAdminOutput'];

export type GetAudienceAdminListQuery = operations['getAudienceAdminList']['parameters']['query'];
export type GetAudienceAdminListResponse =
  operations['getAudienceAdminList']['responses']['200']['content']['application/json'];
export type AudienceSearchOutput = components['schemas']['AudienceSearchOutput'];
export type AudienceAdminOutput = components['schemas']['AudienceAdminOutput'];
export type AudiencePublicMatchOutput = components['schemas']['AudiencePublicMatchOutput'];
export type CreateAudienceUsersAdminPayload =
  operations['createAudienceUsersAdmin']['requestBody']['content']['application/json'];
export type CreateAudienceUsersAdminResponse =
  operations['createAudienceUsersAdmin']['responses']['200']['content']['application/json'];
export type DeleteAudienceUsersAdminPayload =
  operations['deleteAudienceUsersAdmin']['requestBody']['content']['application/json'];
export type DeleteAudienceUsersAdminResponse = unknown;
export type CreateAudienceAdminPayload =
  operations['createAudienceAdmin']['requestBody']['content']['application/json'];
export type CreateAudienceAdminResponse =
  operations['createAudienceAdmin']['responses']['201']['content']['application/json'];
export type UpdateAudienceAdminPayload =
  operations['updateAudienceAdmin']['requestBody']['content']['application/json'];
export type UpdateAudienceAdminResponse =
  operations['updateAudienceAdmin']['responses']['200']['content']['application/json'];

export type ListAudienceUsersResponse =
  operations['getAudienceUsersAdminList']['responses']['200']['content']['application/json'];

export async function listAudiences(
  query: ListAudiencesQuery,
  options?: { signal?: AbortSignal },
): Promise<ListAudiencesResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/audiences?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export type SearchAudienceAdminListResponse = {
  status: number;
  message: string;
  result: {
    audienceList: Array<AudienceSearchOutput>;
  };
};

export async function searchAudienceAdminList(
  query: SearchAudienceAdminListQuery,
  options?: { signal?: AbortSignal },
): Promise<SearchAudienceAdminListResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/admin/audiences/search?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export type GetAudiencePublicListResponse = {
  status: number;
  message: string;
  result: {
    audienceList: Array<AudiencePublicMatchOutput>;
  };
};

export async function getAudiencePublicList(
  query: GetAudiencePublicListQuery,
  options?: { signal?: AbortSignal },
): Promise<GetAudiencePublicListResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/audiences?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function listAudienceUsers(
  query: ListAudienceUsersQuery,
  options?: { signal?: AbortSignal },
): Promise<ListAudienceUsersResponse> {
  return getAudienceUsersAdminList(query, options);
}

export async function getAudienceUsersAdminList(
  query: GetAudienceUsersAdminListQuery,
  options?: { signal?: AbortSignal },
): Promise<GetAudienceUsersAdminListResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/admin/audience-users?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function getAudienceAdminList(
  query: GetAudienceAdminListQuery,
  options?: { signal?: AbortSignal },
): Promise<GetAudienceAdminListResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/admin/audiences?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function createAudienceUsersAdmin(
  payload: CreateAudienceUsersAdminPayload,
  options?: { signal?: AbortSignal },
): Promise<CreateAudienceUsersAdminResponse> {
  return request(`${basePath()}promo/admin/audience-users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function deleteAudienceUsersAdmin(
  payload: DeleteAudienceUsersAdminPayload,
  options?: { signal?: AbortSignal },
): Promise<DeleteAudienceUsersAdminResponse> {
  return request(`${basePath()}promo/admin/audience-users`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function createAudienceAdmin(
  payload: CreateAudienceAdminPayload,
  options?: { signal?: AbortSignal },
): Promise<CreateAudienceAdminResponse> {
  return request(`${basePath()}promo/admin/audiences`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export async function updateAudienceAdmin(
  audienceId: string,
  payload: UpdateAudienceAdminPayload,
  options?: { signal?: AbortSignal },
): Promise<UpdateAudienceAdminResponse> {
  return request(`${basePath()}promo/admin/audiences/${audienceId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
}

export type GetMyVouchersOptions = {
  signal?: AbortSignal;
  /** Customer sites select translated voucher copy through the request locale. */
  locale?: string;
};

export async function getMyVouchers(
  getParams: operations['listMyVouchers']['parameters']['query'],
  options?: GetMyVouchersOptions,
): Promise<operations['listMyVouchers']['responses']['200']['content']['application/json']> {
  return request(`${basePath()}promo/my-vouchers?${qs.stringify(getParams, { skipNulls: true })}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(options?.locale ? { 'Accept-Language': options.locale } : {}),
    },
    signal: options?.signal,
  }) as Promise<operations['listMyVouchers']['responses']['200']['content']['application/json']>;
}

/** Loads an account-facing voucher view for a customer selected by an authorised admin. */
export async function getAdminUserMyVouchers(
  userId: string,
  getParams: operations['listAdminUserMyVouchers']['parameters']['query'],
  options?: GetMyVouchersOptions,
): Promise<operations['listAdminUserMyVouchers']['responses']['200']['content']['application/json']> {
  return request(
    `${basePath()}promo/admin/users/${encodeURIComponent(userId)}/my-vouchers?${qs.stringify(getParams, {
      skipNulls: true,
    })}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.locale ? { 'Accept-Language': options.locale } : {}),
      },
      signal: options?.signal,
    },
  ) as Promise<operations['listAdminUserMyVouchers']['responses']['200']['content']['application/json']>;
}

export async function postMyVouchers(
  payload: operations['listMyVouchersV2']['requestBody']['content']['application/json'],
): Promise<unknown> {
  return request(`${basePath()}promo/my-vouchers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    returnResponse: true,
  });
}

export const promoCreateTemplateSessionKey = 'promo-system-create-template';

export type PromoBrands = definitions['Promo Brands'];

export type PromoMeta = operations['getPromoMeta']['responses']['200']['content']['application/json']['result'];

export const promoTypesForSubscription = (promoTypes: PromoMeta['promo_types'] = []) =>
  promoTypes.filter((type) => type.key !== 'none' && (type.isShownInAdmin || type.isShownInAdmin === undefined));

export const promoTypes = (promoTypes: PromoMeta['promo_types'] = []) =>
  promoTypes.filter((type) => type.isShownInAdmin || type.isShownInAdmin === undefined);

export const promoTypesWithoutNone = (promoTypes: PromoMeta['promo_types'] = []) =>
  promoTypes.filter((type) => type.key !== 'none' && (type.isShownInAdmin || type.isShownInAdmin === undefined));

export const getPromoTypeDisplayName = (promoType: string) => {
  switch (promoType) {
    case 'percentage':
      return 'Percentage';
    case 'fixed_amount':
      return 'Fixed Amount';
    case 'nights-free':
      return 'Nights Free';
    case 'none':
      return 'None';
    default:
      return promoType;
  }
};

export type PromoProductTypes = PromoMeta['promo_product_types'];
export type PromoToggles = PromoMeta['promo_toggles'];

export type PromoRegionGroupings = PromoMeta['promo_region_groupings'];

export type ReferralConfig = definitions['GlobalReferralConfig'];

export type PromoToggleKey = PromoMeta['promo_toggles']['0']['key'];
export type PromoToggleConfig = PromoMeta['promo_toggles'];

export type PromoRequestSource =
  operations['calculateDiscount']['requestBody']['content']['application/json']['requestSource'];

export type UpdatePromoRolePayload = operations['updatePromoRole']['requestBody']['content']['application/json'];

export type PromoRequestWarn = {
  message: string;
  severity: 'error' | 'warning' | 'info';
};

export type PromoLoyaltyCustomRulePresented =
  operations['getLoyaltyPromoKeyOptions']['responses']['200']['content']['application/json']['result']['loyaltyPromoKeyOptions']['0'];

export type PromoLoyaltyCustomRulePresentedList = {
  brand: string;
  region: string;
  promoKey: string;
  customRuleId: string;
  createdBy: string;
  createdAt: string;
  loyaltyCustomRule?: PromoLoyaltyCustomRulePresented;
};

export type PromoConditionRaw = definitions['PromoCodeConditionPresented'];

export type PromoConditionTypeConfig = definitions['PromoConditionConfig'] & {
  conditionApplyTypes?: Array<string>;
};

export type PromoCondition = Omit<definitions['PromoCodeConditionPresented'], 'id_promo_code_condition'> &
  Partial<Pick<definitions['PromoCodeCondition'], 'id_promo_code_condition'>> & {
    isNew: boolean;
    id?: string;
    config?: PromoConditionTypeConfig; // will be removed on 1.7.46 contract publish
  } & {
    // will be removed on 1.7.46 contract publish
    condition_apply_type?: string;
  };

export type InternalPromoWithDiscounts = definitions['InternalPromoWithDiscounts'];

/**
 * We include 'unknown' to account for <PromoLogs/> 'ALL' view which does not have the promo_type readily available (yet)
 */
export type PromoTypesAndUnknown =
  | operations['calculateDiscount']['responses']['200']['content']['application/json']['result']['promo']['promo_type']
  | 'unknown';

export type PromoRequest = definitions['PromoCodeRequest'] & {
  voucherNames?: Array<string>;
};

export type GetPromoUserFilters = operations['getPromoUsers']['parameters']['query'];

export type PromoWarning = definitions['PromoWarning'];

export type PromoUser =
  operations['getPromoUsers']['responses']['200']['content']['application/json']['result']['promoUsers']['0'];

export type GetPromoClonesQuery = operations['getPromoClones']['parameters']['query'];

type RequestItem =
  operations['calculateDiscount']['responses']['200']['content']['application/json']['result']['promo']['items']['0'] & {
    travellers: {
      firstName: string;
      lastName: string;
    };
  };

export type GetPromoRolesQuery = operations['getPromoRoles']['parameters']['query'];

export type DiscountItemInfo = {
  rId: string;
  item: {
    poffer: {
      itemId?: string | null;
      offerId: string;
      productBK: string;
      categoryBK: string;
      isStaffDiscountEnabled: boolean;
    };
    reqItem: RequestItem;
  };
  warning: string;
  discount: number;
  discountAmount: number;
  discountType?: 'percentage' | 'fixed_amount';
  matchingDiscount?: {
    region: string;
    products?: Array<string>;
    min_spend?: number | null;
    created_at: string;
    updated_at: string;
    modified_by: string;
    max_discount?: number | null;
    id_promo_code: string;
    discount_value: number;
    id_promo_code_discount: string;
    subscription_item_discount_type: string;
    subscription_item_discount_value: number;
  };
  luxPlusDiscountHigher: boolean;
  doesNotQualifyWithMemberPricing: boolean;
  discountHasCombinedWithLuxPlusPricing: boolean;
};

type getFilterTitleProps = {
  prefix: string;
  codeName?: string;
  userId?: string;
  includeDeleted?: boolean;
};

export const getFilterTitle = ({ prefix, codeName, userId, includeDeleted }: getFilterTitleProps): string => {
  let title = '';
  if (codeName && codeName?.toLowerCase() !== 'all') {
    title += `for '${codeName}'`;
  }
  if (userId) {
    title += `by ${userId}`;
  }

  if (includeDeleted) {
    title += ` (including deleted)`;
  }

  return `${prefix} ${title.length > 0 ? '' + title : ''}`;
};

export const sortPromoItemByCategoryAndId = (a, b) => {
  if (a.categoryBK === b.categoryBK) {
    const idA = a.offerId || a.itemId || '';
    const idB = b.offerId || b.itemId || '';

    return idA.localeCompare(idB);
  }

  return a.categoryBK.localeCompare(b.categoryBK);
};

export const displayItemType = (i): string => {
  return `${i.categoryBK}
    ${i.parentType !== i.categoryBK ? `/${i.parentType}` : ''}              
    ${i.itemType !== i.categoryBK ? `/${i.itemType}` : ''}`;
};

export const hasAdvanceRestrictions = (promoCode): boolean => {
  if (!promoCode) {
    return false;
  }
  return (
    promoCode?.email_addresses?.length > 0 ||
    promoCode?.email_domains?.length > 0 ||
    promoCode?.offer_whitelist?.length > 0 ||
    promoCode?.offer_blacklist?.length > 0 ||
    promoCode?.bin_numbers?.length > 0 ||
    promoCode?.allowed_payment_methods?.length > 0 ||
    promoCode?.allowed_payment_brands?.length > 0 ||
    promoCode?.promo_toggles?.length > 0 ||
    promoCode?.device_types?.length > 0 ||
    promoCode?.allowed_airline_carriers?.length > 0 ||
    promoCode?.is_corporate
  );
};

export const getPromoRequestWarnings = (promoRequest: definitions['PromoCodeRequest']): Array<PromoRequestWarn> => {
  const warnings: Array<PromoRequestWarn> = [];
  const reqBody = promoRequest.bodyJSON;

  if (!reqBody) {
    return warnings;
  } else {
    if (promoRequest.statusCode === 500) {
      warnings.push({ message: 'Internal Server Error', severity: 'error' });
    }
  }
  return warnings;
};

type PromoItemDataWarning = {
  message: string;
  code: 'LUX_PLUS_PRICE_TOO_HIGH' | 'CATEGORY_BK_MISSING' | 'SUB_CATEGORY_BK_MISSING' | 'NO_TRAVELLERS';
  severity: 'error' | 'warning';
};

export const getPromoItemDataWarnings = (discountItem: DiscountItemInfo) => {
  const errors: Array<PromoItemDataWarning> = [];

  const { luxPlusPrice, discountableTotal } = discountItem.item.reqItem;
  if (
    typeof luxPlusPrice === 'number' &&
    luxPlusPrice > 0 &&
    typeof discountableTotal === 'number' &&
    luxPlusPrice > discountableTotal
  ) {
    errors.push({
      message: `LuxPlusPrice (${luxPlusPrice}) is higher than discountableTotal (${discountableTotal}).`,
      code: 'LUX_PLUS_PRICE_TOO_HIGH',
      severity: 'error',
    });
  }

  if (!discountItem.item.reqItem.categoryBK) {
    errors.push({
      message: `CategoryBK is not present on request item`,
      code: 'CATEGORY_BK_MISSING',
      severity: 'error',
    });
  }

  return errors;
};

interface ProductTypeOption {
  product_bk: string;
  category_bk: string;
  value: definitions['Item Product BK Options'];
  label: string;

  /**
   * @description If true, this option will be hidden from the UI (but still available if already set on the promo code)
   */
  preferToHide?: boolean;
}

/**
 *
 * @param promoProductTypes All promo_product_types from the getPromoMeta endpoint
 * @param filterFields specific product_bk to filter to
 * @param filterType default filtering type (either for min_spend_products or products)
 * @returns Array<ProductTypeOption>
 */
export const getProductTypeOptions = (
  promoProductTypes: PromoProductTypes,
  filterFields: Array<string> = [],
): Array<ProductTypeOption> => {
  return promoProductTypes.flatMap((ppt) => {
    const prods = ppt.products
      .filter((p) => !p.dontShowInAdmin)
      .filter((p) => filterFields.length === 0 || filterFields.includes(p.product_bk));

    return prods.map((product) => {
      const value = `${ppt.category_bk}-${product.product_bk}`;
      return {
        product_bk: product.product_bk,
        category_bk: ppt.category_bk,
        value: value as definitions['Item Product BK Options'],
        label: product.productAdminName,
      };
    });
  });
};

export const getMinSpendProductTypeOptions = (
  promoProductTypes: PromoProductTypes,
  filterFields: Array<string> = [],
): Array<ProductTypeOption> => {
  return promoProductTypes
    .filter((category) => category.canUseForProductLevelMinSpend)
    .flatMap((ppt) => {
      const prods = ppt.products
        .filter((p) => p.canUseForProductLevelMinSpend)
        .filter((p) => filterFields.length === 0 || filterFields.includes(p.product_bk));

      return prods.map((product) => {
        const value = `${ppt.category_bk}-${product.product_bk}`;
        return {
          product_bk: product.product_bk,
          category_bk: ppt.category_bk,
          value: value as definitions['Item Product BK Options'],
          label: product.productAdminName,
        };
      });
    });
};

export function getPromoProductDisplayName(
  promoProductTypes: PromoProductTypes,
  categoryBK: string,
  subCategoryBK?: string,
): string {
  if (!promoProductTypes) {
    return `${categoryBK}-${subCategoryBK}....`;
  }

  const productMatch = promoProductTypes
    .flatMap((ppt) => ppt.products)
    .find((p) => {
      return subCategoryBK === `${categoryBK}-${p.product_bk}`;
    });
  if (productMatch) {
    return productMatch.productAdminName;
  }

  const categoryMatch = promoProductTypes.find((ppt) => ppt.category_bk === categoryBK);
  if (categoryMatch) {
    return categoryMatch.categoryName;
  }

  return `${categoryBK}-${subCategoryBK}`;
}

/**
 * @deprecated Use createOrUpdatePromo instead
 */
export async function addPromo(object) {
  return request(basePath() + 'promo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(object),
  });
}

export async function createOrUpdatePromo(payload: CreateOrUpdatePromoPayload): Promise<CreateOrUpdatePromoResponse> {
  return request(basePath() + 'promo/v2', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/** Mirrors www-le-customer's Friends & Family restricted-promo request. */
export async function requestRestrictedPromo(
  payload: RequestRestrictedPromoPayload,
): Promise<RequestRestrictedPromoResponse> {
  return request(basePath() + 'promo/request-restricted-promo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function getFriendsAndFamilyUsage(
  query: GetFriendsAndFamilyUsageQuery,
  options?: { signal?: AbortSignal },
): Promise<GetFriendsAndFamilyUsageResponse> {
  return request(`${basePath()}promo/friends-and-family-usage?${qs.stringify(query)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export type BulkDeletePromoCodesByIdsPayload =
  operations['deletePromoCodesByIds']['requestBody']['content']['application/json'];
export type BulkDeletePromoCodesByIdsResponse =
  operations['deletePromoCodesByIds']['responses']['200']['content']['application/json'];

// Promo deletion can cascade to related records and may take several minutes.
// Give the browser a clear long-running deadline so it can receive the operation result.
const BULK_PROMO_DELETE_TIMEOUT_MS = 10 * 60 * 1000;

/** Deletes the explicitly selected promo IDs. */
export async function bulkDeletePromoCodesByIds(
  payload: BulkDeletePromoCodesByIdsPayload,
): Promise<BulkDeletePromoCodesByIdsResponse> {
  return request(basePath() + 'promo/by-ids', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(BULK_PROMO_DELETE_TIMEOUT_MS),
  });
}

export async function checkPromoRequest(query: CheckPromoQuery) {
  const queryString = new URLSearchParams(
    Object.entries(query)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  return request(basePath() + 'promo/check?' + queryString, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export type PromoRoleFilters = {
  page?: number;
  limit?: number;
  includeInactive: boolean;
  extendedResponse: boolean;
};

export type PromoRolesResponse = {
  status: number;
  result: {
    roles: Array<PromoRole>;
    total: number;
  };
};

export type PromoRoleModifyResponse = operations['updatePromoRole']['responses']['200']['content']['application/json'];
export type UsePromoRoleProps = {
  initialFilters?: PromoRoleFilters;
};

export type PromoUnassignResponse =
  operations['unassignPromoRoleUser']['responses']['204']['content']['application/json'];

export const promoRoles = {
  PROMO_BETA_TESTER_ROLE: 'Promo-Beta-Tester',
  PROMO_VIP_ROLE: 'Promo-VIP',
};

export type PromoRoleKeys = keyof typeof promoRoles;

export async function getPromoRoles(filters: PromoRoleFilters): Promise<PromoRolesResponse> {
  const query: GetPromoRolesQuery = {
    page: filters.page?.toString(),
    limit: filters.limit?.toString(),
    includeInactive: filters.includeInactive,
    extendedResponse: filters.extendedResponse,
  };
  const url = `${basePath()}promo/roles?${QueryString.stringify(query)}`;

  return request(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function updatePromoRole(roleId: string, updates: Partial<PromoRole>): Promise<PromoRoleModifyResponse> {
  const payload: UpdatePromoRolePayload = {
    description: updates.description,
    role_name: updates.roleName,
    is_active: updates.isActive,
  };
  return request(`${basePath()}promo/roles/${roleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export type AddPromoUserToRoleResult =
  operations['assignPromoRoleUser']['responses']['201']['content']['application/json'];

export async function addPromoUserToRole(roleId: string, userId: string): Promise<AddPromoUserToRoleResult> {
  return request(`${basePath()}promo/roles/${roleId}/user/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function unassignPromoRole(roleId: string, userId: string): Promise<PromoUnassignResponse> {
  return request(`${basePath()}promo/roles/${roleId}/user/${userId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

type PromoDeleteResponse = operations['deletePromoRole']['responses']['200']['content']['application/json'];

export async function deletePromoRole(roleId: string): Promise<PromoDeleteResponse> {
  return request(`${basePath()}promo/roles/${roleId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export type CreatePromoRolePayload = operations['createPromoRole']['requestBody']['content']['application/json'];

export type CreatePromoRoleResponse = operations['createPromoRole']['responses']['200']['content']['application/json'];

export async function createPromoRole(role: PromoRoleCreateProps): Promise<CreatePromoRoleResponse> {
  const payload: CreatePromoRolePayload = {
    description: role.description,
    is_active: false,
    role_name: role.roleName,
  };
  return request(`${basePath()}promo/roles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export type PromoRoleCreateProps = Omit<
  PromoRole,
  'roleId' | 'createdAt' | 'createdBy' | 'updatedAt' | 'modifiedBy' | 'isSystem'
>;

export type LoyaltyPointsBonusConfigV1 = {
  loyaltyPointsBonusConfigV1: Array<{
    id: string;
    loyaltyPromoKey: string;
    loyaltyProductType: string;
    applyOrder?: number;
  }>;
};

export const mapLoyaltyPointsBonusConfigToV1 = (
  config: definitions['Internal Promo Discount']['loyalty_points_bonus_config'],
): LoyaltyPointsBonusConfigV1 => {
  return {
    loyaltyPointsBonusConfigV1:
      config?.loyaltyPointsBonusConfigV1?.map((item) => ({
        id: item?.id ?? uuidv4(),
        loyaltyPromoKey: item.loyaltyPromoKey,
        loyaltyProductType: item.loyaltyProductType,
        applyOrder: Number(item.applyOrder) || 0,
      })) ?? [],
  };
};

export type PromoLoyaltyMetaConfig = definitions['PromoMeta']['promo_loyalty_config'];

export const getProductTypeLabel = (type: string, promoLoyaltyMetaConfig: PromoLoyaltyMetaConfig) => {
  const option = promoLoyaltyMetaConfig.loyaltyProductTypeOptions.find((opt) => opt.key === type);
  return option?.name || type;
};

export type PromoRole = {
  roleId: string;
  roleName: string;
  description: string;
  isActive: boolean;
  isSystem: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  modifiedBy?: string;
  users?: Array<PromoRoleUser>;
};

export type PromoRoleUser = {
  userId: string;
  updatedAt: string;
};

export type CreatePromoUserPayload =
  operations['createOrUpdatePromoCodeUsers']['requestBody']['content']['application/json'];

export type CreatePromoUserPayloadUser =
  operations['createOrUpdatePromoCodeUsers']['requestBody']['content']['application/json']['promoUsers']['0'];

export type PromoCodeConditionMutationPayload = {
  promoCodeCondition: NonNullable<CreateOrUpdatePromoPayload['promoCondition']>;
};
export type PromoCodeConditionMutationResponse = {
  status: CreateOrUpdatePromoResponse['status'];
  message?: CreateOrUpdatePromoResponse['message'];
  result: {
    promoCodeCondition: PromoConditionRaw;
  };
  count?: CreateOrUpdatePromoResponse['count'];
  nextPage?: CreateOrUpdatePromoResponse['nextPage'];
};
export type PromoCodeDiscountMutationPayload = NonNullable<CreateOrUpdatePromoPayload['promoDiscount']>;
export type PromoCodeDiscountMutationResponse = {
  status: CreateOrUpdatePromoResponse['status'];
  message?: CreateOrUpdatePromoResponse['message'];
  result: {
    promoDiscount?: InternalPromoWithDiscounts['promo_discounts'][number];
    promoDiscounts?: InternalPromoWithDiscounts['promo_discounts'];
  };
  count?: CreateOrUpdatePromoResponse['count'];
  nextPage?: CreateOrUpdatePromoResponse['nextPage'];
};

export async function createOrUpdatePromoUsers(payload: CreatePromoUserPayload) {
  if (payload.promoUsers.length !== 1 && payload.promoUsers[0].id_promo_code_user) {
    throw new Error('Only one promo user can be updated at a time');
  }

  const url = `${basePath()}${
    payload.promoUsers[0].id_promo_code_user ? `promo/users/${payload.promoUsers[0].id_promo_code_user}` : 'promo/users'
  }`;
  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function createOrUpdatePromoCondition(
  payload: PromoCodeConditionMutationPayload,
): Promise<PromoCodeConditionMutationResponse> {
  const response = await createOrUpdatePromo({
    promo: {
      id_promo_code: payload.promoCodeCondition.id_promo_code,
    },
    promoCondition: payload.promoCodeCondition,
    mode: 'create-or-update',
  });

  const promoCodeCondition = response.result.conditionsUpdated[0];

  if (!promoCodeCondition) {
    throw new Error('Condition not found in promo/v2 response');
  }

  return {
    status: response.status,
    message: response.message,
    result: {
      promoCodeCondition,
    },
    count: response.count,
    nextPage: response.nextPage,
  };
}

export const ensureCodeNameIsValid = (codeName: string | undefined): string | null => {
  if (!codeName) {
    return null;
  }
  if (!/^[A-Za-z0-9_-]+$/.test(codeName)) {
    return `Name (${codeName}) can only include alphanumeric characters, underscores, and dashes`;
  }
  return null;
};

export function deletePromoCodeCondition(id: string) {
  return request(`${basePath()}promo/condition/${id}`, {
    method: 'DELETE',
  });
}

export type GetPlaceIdsAtCoordinateResponse =
  operations['getPlaceIdsAtCoordinate']['responses']['200']['content']['application/json'];

/** GET /api/promo/places/at-coordinate — places containing a lat/lng (e.g. for placeId condition values). */
export function getPromoPlacesAtCoordinate(lat: number, lng: number): Promise<GetPlaceIdsAtCoordinateResponse> {
  const query = new URLSearchParams({ lat: String(lat), lng: String(lng) });
  return request(`${basePath()}promo/places/at-coordinate?${query}`, { method: 'GET' });
}

export function deletePromoUser(id: string) {
  return request(`${basePath()}promo/users/${id}`, {
    method: 'DELETE',
  });
}

// Region/channel settings — marketing settings scoped by region + UTM channel, discriminated by
// setting_type.
// NOTE: typed locally until `@luxuryescapes/contract-svc-promo` is regenerated with the
// `/api/promo/region-channel-setting` endpoints; then switch to `operations`/`definitions`.
// Must stay in step with REGION_CHANNEL_SETTING_TYPES in svc-promo — that enum is what validates
// a create/update, so a type listed here before svc-promo ships it gets rejected at save time.

export type RegionChannelSettingType =
  | 'free_preview_auto_enrol'
  | 'free_preview_auto_enrol_for_existing_users'
  | 'offer_region_filter_bypass';

export interface RegionChannelSetting {
  region_channel_setting_id: string;
  setting_type: RegionChannelSettingType;
  region_code?: string | null;
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  term?: string | null;
  content?: string | null;
  enabled: boolean;
  start_datetime?: string | null;
  end_datetime?: string | null;
  created_by: string;
  created_at: string;
  expires_at?: string | null;
}

export interface ListRegionChannelSettingsQuery {
  setting_type?: RegionChannelSettingType;
  region_code?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  page?: number;
  pageSize?: number;
  status?: 'active' | 'expired' | 'all';
}

export interface ListRegionChannelSettingsResponse {
  status: number;
  message: string;
  result: {
    regionChannelSettingList: Array<RegionChannelSetting>;
    total: number;
  };
}

export interface RegionChannelSettingPayload {
  setting_type: RegionChannelSettingType;
  region_code?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  enabled?: boolean;
  start_datetime?: string;
  end_datetime?: string;
}

export interface RegionChannelSettingResponse {
  status: number;
  message: string;
  result: RegionChannelSetting;
}

export interface DeleteRegionChannelSettingResponse {
  status: number;
  message: string;
}

export interface ResolveRegionChannelSettingsQuery {
  region?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
}

export interface RegionChannelSettingResolution {
  setting_type: RegionChannelSettingType;
  enabled: boolean;
  setting_id?: string | null;
  end_datetime?: string | null;
  /** Only on the offer_region_filter_bypass entry. */
  excluded_offer_ids?: Array<string>;
}

export interface ResolveRegionChannelSettingsResponse {
  status: number;
  message: string;
  result: Array<RegionChannelSettingResolution>;
}

/**
 * Resolves every setting type for one customer's region + traffic channel — the same endpoint the
 * customer portal calls, so what it returns is what a real visitor would get.
 *
 * Note this response is cached for 10 minutes at the CDN, so a rule saved moments ago may not be
 * reflected yet. The Resolve tab says so on screen.
 */
export async function resolveRegionChannelSettings(
  query: ResolveRegionChannelSettingsQuery,
  options?: { signal?: AbortSignal },
): Promise<ResolveRegionChannelSettingsResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/region-channel-setting/v2?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function listRegionChannelSettings(
  query: ListRegionChannelSettingsQuery,
  options?: { signal?: AbortSignal },
): Promise<ListRegionChannelSettingsResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/region-channel-setting/list?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function getRegionChannelSetting(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<RegionChannelSettingResponse> {
  return request(`${basePath()}promo/region-channel-setting/${id}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function createRegionChannelSetting(
  payload: RegionChannelSettingPayload,
): Promise<RegionChannelSettingResponse> {
  return request(`${basePath()}promo/region-channel-setting`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function updateRegionChannelSetting(
  id: string,
  payload: RegionChannelSettingPayload,
): Promise<RegionChannelSettingResponse> {
  return request(`${basePath()}promo/region-channel-setting/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function deleteRegionChannelSetting(id: string): Promise<DeleteRegionChannelSettingResponse> {
  return request(`${basePath()}promo/region-channel-setting/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export function prepareLoyaltyPointsBonusConfig(
  loyaltyPointsBonusConfig: definitions['Internal Promo Discount']['loyalty_points_bonus_config'],
): LoyaltyPointsBonusConfigV1 | undefined {
  if (!loyaltyPointsBonusConfig) {
    return undefined;
  }
  return {
    loyaltyPointsBonusConfigV1:
      loyaltyPointsBonusConfig.loyaltyPointsBonusConfigV1?.map((item) => ({
        id: item?.id ?? uuidv4(),
        loyaltyPromoKey: item.loyaltyPromoKey,
        loyaltyProductType: item.loyaltyProductType,
        applyOrder: Number(item.applyOrder) || 0,
      })) ?? [],
  };
}

export type GetPromoCodeProps = {
  page: number;
  codeName: string;
  emailAddress: string;
  brand: string;
  limit: number;
  caseId: string;
  expiresAfter: string;
  noChildPromos: boolean;
  onlyChildPromos: boolean;
  excludeReferralPromos?: boolean;
  excludeApprovedPromos?: boolean;
  userIdInteracted?: string;
  deptTag: string;
  categoryTag: string;
  subCategoryTag: string;
  promoCategory?: PromoCategory;
  dev: boolean;
  onlyParentPromos?: boolean;
};

export async function getPromoCodes(props: GetPromoCodeProps) {
  const query: PromoSearchQuery = {
    page: props.page?.toString(),
    limit: props.limit?.toString(),
    brand: props.brand as operations['searchPromos']['parameters']['query']['brand'],
    code_name: props.codeName,
    dept_tag: props.deptTag,
    category_tag: props.categoryTag,
    sub_category_tag: props.subCategoryTag,
    exclude_approved_promos: props.excludeApprovedPromos ? 'true' : 'false',
  };

  if (props.page == undefined) {
    query.page = '1';
  }
  if (!props.limit) {
    query.limit = '10';
  }
  if (!props.brand) {
    query.brand = 'luxuryescapes';
  }
  if (props.userIdInteracted && props.userIdInteracted.length > 0) {
    query.user_id_interacted = props.userIdInteracted;
  }
  if (props.caseId?.length > 0) {
    query.case_id = props.caseId;
  }

  if (props.emailAddress?.length > 0) {
    query.email_address = props.emailAddress;
  }
  if (props.expiresAfter?.length > 0) {
    query.expires_after = props.expiresAfter;
  }

  if (props.noChildPromos) {
    query.exclude_child_promos = props.noChildPromos ? 'true' : 'false';
  }
  if (props.onlyChildPromos) {
    query.only_child_promos = 'true';
  }
  if (props.onlyParentPromos) {
    query.only_parent_promos = 'true';
  }
  if (props.promoCategory) {
    query.promo_category = props.promoCategory;
  }
  if (props.excludeReferralPromos) {
    query.exclude_referral_promos = props.excludeReferralPromos ? 'true' : 'false';
  }

  if (props.excludeApprovedPromos) {
    query.exclude_approved_promos = props.excludeApprovedPromos ? 'true' : 'false';
  }

  if (props.deptTag) {
    query.dept_tag = props.deptTag;
  }

  if (props.categoryTag) {
    query.category_tag = props.categoryTag;
  }
  if (props.subCategoryTag) {
    query.sub_category_tag = props.subCategoryTag;
  }
  const queryString = qs.stringify(query);
  return request(`${basePath()}promo/v2/codes?${queryString}`, { method: 'GET' });
}

/**
 * @deprecated This legacy endpoint does not support orders with multiple items, or promos with multiple discounts. The log endpoint will always apply new server-side validation and order placed with promos via this endpoint can cause "Promo amount does not match the promo definition" errors. Use with extreme caution and consider the /api/promo/discount endpoint instead.
 */
export async function getPromo(object) {
  const queryParams = {
    brand: object.brand,
    user_id: object.user_id,
  };
  const queryString = qs.stringify(queryParams);
  return request(`${basePath()}promo/codes/${object.code_name}?${queryString}`, {
    method: 'GET',
  });
}

export async function approvePromo(codeName: string, brand: string) {
  const queryString = qs.stringify({ codeName, brand });
  return request(`${basePath()}promo/approve?${queryString}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function unapprovePromo(codeName: string, brand: string) {
  const queryString = qs.stringify({ codeName, brand });
  return request(`${basePath()}promo/approve?${queryString}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}
interface GetInternalPromoProps {
  code_name: string;
  brand: string;
}

export async function getInternalPromo({ brand, code_name }: GetInternalPromoProps) {
  const queryParams = {
    brand: brand,
  };
  const encodedCodeName = encodeURIComponent(code_name);
  const queryString = qs.stringify(queryParams);
  return request(`${basePath()}promo/code/${encodedCodeName}?${queryString}`, {
    method: 'GET',
  });
}

type getPromoChildrenProps = {
  id_promo_code: string;
  page: number;
  limit: number;
  only_count?: boolean;
};

export async function getPromoClones({ id_promo_code, page, limit, only_count }: getPromoChildrenProps) {
  const queryParams: GetPromoClonesQuery = {
    page: page.toString(),
    limit: limit.toString(),
    onlyCount: only_count ? 'true' : 'false',
  };
  const queryString = qs.stringify(queryParams);
  return request(`${basePath()}promo/id/${id_promo_code}/clones?${queryString}`, {
    method: 'GET',
    accept: 'application/json',
  });
}

export type BulkCreatePromosProps = {
  promo_prefix?: string;
  number_to_create: number;
  expires_at?: string;
  id_promo_code: string;
  random_post_fix_length?: number;
  /** When true, sent as `emancipate_child_promos` on the bulk create API. */
  emancipate_child_promos?: boolean;
  signal?: AbortSignal;
};

export type BulkCreatePromosResult = {
  status: number;
  result: {
    promos: Array<{
      code_name: string;
      expires_at: string;
    }>;
  };
};

type ItemInfoMeta = {
  promo_code_discount_amount: number;
};

export const exportItemInfo = (itemInfoString: string): ItemInfoMeta | null => {
  if (!itemInfoString) {
    return null;
  }
  if (itemInfoString.includes('item.promo_code_discount_amount')) {
    const [, promo_code_discount_amount] = itemInfoString.split('item.promo_code_discount_amount:');
    return {
      promo_code_discount_amount: parseFloat(promo_code_discount_amount),
    };
  }
  return null;
};

export async function bulkCreatePromos({
  id_promo_code,
  number_to_create,
  expires_at,
  promo_prefix,
  random_post_fix_length,
  emancipate_child_promos,
  signal,
}: BulkCreatePromosProps): Promise<BulkCreatePromosResult> {
  const response: operations['bulkCreatePromo']['responses']['200']['content']['application/json'] = await request(
    `${basePath()}promo/id/${id_promo_code}/bulk`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({
        promo_prefix,
        number_to_create,
        expires_at,
        random_post_fix_length,
        ...(emancipate_child_promos ? { emancipate_child_promos: true } : {}),
      }),
    },
  );
  return {
    status: response.status,
    result: {
      promos: response.result.promos,
    },
  };
}

type BulkCloneResponse = operations['bulkClonePromo']['responses']['201']['content']['application/json'];
export async function bulkClonePromos({
  promoCodeParentId,
  promoCodeChildrenNames,
  expiresAt,
  totalUsageLimit,
  emancipateChildPromos,
}: {
  promoCodeParentId: string;
  promoCodeChildrenNames: Array<string>;
  expiresAt?: Date;
  totalUsageLimit?: number;
  emancipateChildPromos?: boolean;
}): Promise<BulkCloneResponse> {
  return request(`${basePath()}promo/id/${promoCodeParentId}/bulk/clone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      promoCodeChildrenNames,
      expiresAt: expiresAt?.toISOString(),
      codeLimit: totalUsageLimit,
      ...(emancipateChildPromos ? { emancipateChildPromos: true } : {}),
    }),
  });
}

export async function getReferralPromo({ codeName, regionCode, brand, customerId, cartValue }) {
  return request(`${basePath()}referral/promo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code: codeName,
      country_code: regionCode,
      brand,
      id_account: customerId,
      cart_value: cartValue,
    }),
  });
}

export async function getPromoById({
  idPromo,
  brand,
  signal,
}: {
  idPromo: string;
  brand?: PromoBrands;
  signal?: AbortSignal;
}) {
  return request(`${basePath()}promo/id/${idPromo}?${qs.stringify({ brand })}`, {
    method: 'GET',
    signal,
  });
}

export type EmancipateChildPromoResponse = {
  status: number;
  message: string;
  result: {
    id_promo_code: string;
    previous_parent_id_promo_code?: string;
  };
};

/** POST /api/promo/id/:id/emancipate — clears clone lineage so usage logs apply only to this code (not parent/siblings). */
export async function emancipateChildPromo(idPromoCode: string): Promise<EmancipateChildPromoResponse> {
  return request(`${basePath()}promo/id/${idPromoCode}/emancipate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export type PromoResponseJSON = {
  promo: {
    loyaltyPointsBonusResultV1: {
      resultCode: string;
      loyaltyBonusFilterMethod: string;
      loyaltyCalculationResponseItems: Array<{
        itemId: string;
        promoKey: string;
        loyaltyProductType: string;
        price: number;
        offerId: string;
        packageId: string;
      }>;
    };
  };
};

type getLogsByPromoProps = {
  codeName: string;
  brand: definitions['Promo Brands'];
  userId?: string;
  includeDeleted?: boolean;
  page?: number;
  limit?: number;
  logBefore?: Date;
  logAfter?: Date;
};

/** svc-promo `getLogsV2` row; UI adds promo context and normalises `deleted` for the grid */
export type PromoLog = Omit<definitions['Promo log row'], 'deleted'> & {
  promo_code_name?: string;
  promo_type?: string;
  deleted: Date | null;
};

export type PromoLogFlatItem = definitions['Promo log flat item'];

export type GetLogsByPromoResponse = operations['getLogsV2']['responses']['200']['content']['application/json'];

type GetLogsV2Query = operations['getLogsV2']['parameters']['query'];

export async function getLogsByPromo({
  codeName,
  brand,
  userId,
  includeDeleted = false,
  page = 0,
  limit = 10,
  logBefore,
  logAfter,
}: getLogsByPromoProps): Promise<GetLogsByPromoResponse> {
  const params: GetLogsV2Query = {
    brand,
    userId: userId ?? '',
    includeDeleted,
    page,
    limit,
  };

  if (logBefore) {
    params.logBefore = logBefore.toISOString();
  }
  if (logAfter) {
    params.logAfter = logAfter.toISOString();
  }

  return request(`${basePath()}promo/v2/log/${encodeURIComponent(codeName)}?${qs.stringify(params)}`, {
    method: 'GET',
  });
}

export async function debugPromoByOrderID(
  codeName: string,
  orderId: string,
  brand: string,
  useOrignalUserAccount: boolean,
  ignoreLuxPlusPricing: boolean,
  errorBodyCallback: (body: any) => void,
  voucherIds?: Array<string>,
) {
  const query: Record<string, unknown> = {
    brand,
    codeName,
    useOrignalUserAccount,
    ignoreLuxPlusPricing,
  };
  if (voucherIds && voucherIds.length > 0) {
    query.voucherIds = voucherIds;
  }

  const params = qs.stringify(query, { arrayFormat: 'brackets', skipNulls: true });

  return request(`${basePath()}promo/discount/orderId/${orderId}?${params}`, {
    method: 'GET',
    errorBodyCallback: errorBodyCallback,
  });
}

export type PromoRequestFilters = {
  codeName?: string;
  userId?: string;
  brand: definitions['Promo Brands'];
  errorCode?: string;
  page?: number;
  limit?: number;
  voucherName?: string;
  /** `getPromoRequests` query; filters logged promo code requests by body `requestSource` when the API supports it. */
  requestSource?: string;
  /** `getPromoRequests` query; filters requests by types present in the discount input. */
  inputType?: Array<PromoRequestInputType>;
  /** `getPromoRequests` query; filters requests by types present in the discount output. */
  outputType?: Array<PromoRequestOutputType>;
};

export type PromoRequestInputType = NonNullable<
  operations['getPromoRequests']['parameters']['query']['inputType']
>[number];

export type PromoRequestOutputType = NonNullable<
  operations['getPromoRequests']['parameters']['query']['outputType']
>[number];

export const PROMO_REQUEST_INPUT_TYPE_OPTIONS = [
  'promo',
  'voucher',
  'channel-markup',
  'channel-markup-v2',
] as const satisfies ReadonlyArray<PromoRequestInputType>;

export const PROMO_REQUEST_OUTPUT_TYPE_OPTIONS = [
  'promo',
  'voucher',
] as const satisfies ReadonlyArray<PromoRequestOutputType>;

export type PromoRequestRes = {
  status: number;
  message?: string;
  result: {
    promoRequests: Array<PromoRequest>;
  };
  total: number;
};

export function getPromoCodeRequestsParams(
  filters: PromoRequestFilters,
): operations['getPromoRequests']['parameters']['query'] & { voucherName?: string; requestSource?: string } {
  const config: operations['getPromoRequests']['parameters']['query'] & {
    voucherName?: string;
    requestSource?: string;
  } = {
    page: filters.page != null ? filters.page.toString() : undefined,
    limit: filters.limit ? filters.limit.toString() : undefined,
  };

  if (filters.userId) {
    config.userId = filters.userId;
  }
  if (filters.codeName) {
    config.codeName = filters.codeName;
  }
  if (filters.voucherName) {
    config.voucherName = filters.voucherName;
  }

  if (filters.errorCode) {
    config.errorCode = filters.errorCode as operations['getPromoRequests']['parameters']['query']['errorCode'];
  }

  if (filters.brand) {
    config.brand = filters.brand;
  }
  if (filters.requestSource) {
    config.requestSource =
      filters.requestSource as operations['getPromoRequests']['parameters']['query']['requestSource'];
  }
  if (filters.inputType?.length) {
    config.inputType = filters.inputType;
  }
  if (filters.outputType?.length) {
    config.outputType = filters.outputType;
  }
  return config;
}

export type PromoFailure =
  operations['getPromoLogFailures']['responses']['200']['content']['application/json']['result']['promoFailures'];

export type PromoFailureRes = {
  status: number;
  message?: string;
  count: number;
  result: {
    promoFailures: Array<PromoFailure>;
    promoFailureMetrics: operations['getPromoLogFailures']['responses']['200']['content']['application/json']['result']['promoFailureMetrics'];
  };
  total: number;
};

export type PromoFailureFilters = operations['getPromoLogFailures']['parameters']['query'];

export async function getPromoCodeFailures(qp: PromoFailureFilters): Promise<PromoFailureRes> {
  const queryParams = qs.stringify(getPromoCodeFailuresParams(qp));
  return request(`${basePath()}promo/failures?${queryParams}`, {
    method: 'GET',
  });
}

export type ProcessReferralLogFilters = operations['processReferralLogs']['requestBody']['content']['application/json'];

export async function processReferralLogs(payload: ProcessReferralLogFilters) {
  return await request(`${basePath()}referral/logs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function getPromoCodeFailuresParams(
  filters: PromoFailureFilters,
): operations['getPromoLogFailures']['parameters']['query'] {
  const qs: operations['getPromoLogFailures']['parameters']['query'] = {
    page: filters.page,
    limit: filters.limit,
    brand: filters.brand,
  };

  if (filters.user_id) {
    qs.user_id = filters.user_id;
  }
  if (filters.code_name) {
    qs.code_name = filters.code_name;
  }

  if (filters.load_metrics) {
    qs.load_metrics = filters.load_metrics;
  }

  if (filters.error_level) {
    qs.error_level = filters.error_level;
  }

  if (filters.brand) {
    qs.brand = filters.brand;
  }

  if (filters.region) {
    qs.region = filters.region;
  }
  return qs;
}

export async function getPromoCodeRequests(
  qp: PromoRequestFilters,
  options?: { signal?: AbortSignal },
): Promise<PromoRequestRes> {
  const queryParams = qs.stringify(getPromoCodeRequestsParams(qp), { arrayFormat: 'brackets' });
  return request(`${basePath()}promo/requests?${queryParams}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

/**
 * @deprecated - see usePromo hook
 */
export async function updatePromo(id, brand, promoData) {
  return request(`${basePath()}promo/id/${id}?brand=${brand}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(promoData),
  });
}

export async function expirePromo(id, brand) {
  return createOrUpdatePromo({
    promo: {
      id_promo_code: id,
      brand,
      expires_at: dateNow().toISOString(),
    },
    mode: 'update-only',
  });
}

export async function getGiftCardLogs(giftCardCode, brand) {
  return request(`${basePath()}gift-card/log/${giftCardCode}?${qs.stringify({ brand })}`, {
    method: 'GET',
  });
}

export async function searchGiftCardLogs({
  code,
  addedBy,
  redeemedBy,
  pageNum,
  pageSize,
  brand,
}: {
  code?: string;
  addedBy?: string;
  redeemedBy?: string;
  pageNum?: number;
  pageSize?: number;
  brand: string;
}): Promise<operations['searchGiftCardLogs']['responses']['200']['content']['application/json']> {
  return request(
    `${basePath()}gift-card/search-log?${qs.stringify(
      { code, addedBy, redeemedBy, pageNum, pageSize, brand },
      { skipNulls: true },
    )}`,
    {
      method: 'GET',
    },
  );
}

export async function searchBlackhawkGiftCardLogs({
  code,
  userId,
  pageNum,
  pageSize,
  brand,
}: {
  code?: string;
  userId?: string;
  pageNum?: number;
  pageSize?: number;
  brand: string;
}): Promise<operations['searchBlackhawkGiftCardLogs']['responses']['200']['content']['application/json']> {
  return request(
    `${basePath()}gift-card/blackhawk/log?${qs.stringify(
      { code, userId, pageNum, pageSize, brand },
      { skipNulls: true },
    )}`,
    {
      method: 'GET',
    },
  );
}

export async function getGiftCardBalance({
  code,
  pin,
  region,
  brand,
  userId,
}: {
  code: string;
  pin?: string;
  region?: string;
  brand: string;
  userId?: string;
}): Promise<operations['/api/gift-card/code/{code}/balance/post']['responses']['200']['content']['application/json']> {
  return request(
    `${basePath()}gift-card/code/${encodeURIComponent(code)}/balance?${qs.stringify(
      { region, brand },
      { skipNulls: true },
    )}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(pin ? { pin } : {}),
        ...(userId ? { user_id: userId } : {}),
      }),
    },
  );
}

export async function getGiftCards({
  code,
  addedBy,
  redeemedBy,
  pageNum = 0,
  pageSize = 20,
  brand,
  email,
}: {
  code?: string;
  addedBy?: string;
  redeemedBy?: string;
  email?: string;
  pageNum: number;
  pageSize: number;
  brand: string;
}): Promise<operations['getGiftCards']['responses']['200']['content']['application/json']> {
  return request(
    `${basePath()}gift-card?${qs.stringify(
      { code, addedBy, redeemedBy, pageNum, pageSize, brand, email },
      { skipNulls: true },
    )}`,
    {
      method: 'GET',
    },
  );
}

export async function redeemGiftCard(code, customerId, brand) {
  return request(`${basePath()}gift-card/code/${code}/redeem?${qs.stringify({ brand })}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customerIdFromAdmin: customerId,
      gift_card_code: code,
    }),
  });
}

export async function updateGiftCardStatus({
  code,
  status,
}: {
  code: string;
  status: operations['/api/gift-card/code/{code}/patch']['requestBody']['content']['application/json']['gift_card_status'];
}): Promise<operations['/api/gift-card/code/{code}/patch']['responses']['200']['content']['application/json']> {
  return request(`${basePath()}gift-card/code/${code}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      gift_card_status: status,
      gift_card_code: code,
    }),
  });
}

export async function listInAppPromoMessage(brand, region, status, startAfter, endBefore) {
  return request(
    `${basePath()}promo/feature-schedule/list?${qs.stringify({
      brand,
      region,
      active: status,
      startAfter: formatDateDateTimeWidget(startAfter),
      endBefore: formatDateDateTimeWidget(endBefore),
    })}`,
    {
      method: 'GET',
    },
  );
}

export async function getSchemaForInAppMessagePromo() {
  return fetchAndSanitizeSchema(`${basePath()}promo/feature-schedule`);
}

export async function createOrUpdateInAppMessagePromo(
  id,
  codeName,
  region,
  status,
  brand,
  startAt,
  endAt,
  heading,
  details,
) {
  return request(`${basePath()}promo/feature-schedule`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      featuredPromoSchedule: {
        id,
        codeName,
        region,
        active: status,
        brand,
        startAt: formatDateDateTimeWidget(startAt),
        endAt: formatDateDateTimeWidget(endAt),
        heading,
        details,
      },
    }),
  });
}

export async function deleteInAppMessagePromoById(id) {
  return request(`${basePath()}promo/feature-schedule/${id}`, {
    method: 'DELETE',
    ignoreResponse: true,
  });
}

export async function addCompedPackage(object) {
  return request(basePath() + 'promo/comped-package', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(object),
  });
}

export interface CompedPackagesSearchParams {
  brand?: string;
  page?: number;
  limit?: number;
  userQuery?: string;
  offerQuery?: string;
  status?: string;
  description?: string;
}

export async function getCompedPackages(
  brand = 'luxuryescapes',
  page = 1,
  limit = 10,
  searchParams?: Partial<CompedPackagesSearchParams>,
) {
  const params: CompedPackagesSearchParams = {
    brand,
    page,
    limit,
    ...searchParams,
  };

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value !== undefined && value !== ''),
  );

  return request(`${basePath()}promo/comped-package?${qs.stringify(filteredParams)}`, {
    method: 'GET',
  });
}

export async function getCompedPackageById(id, brand = 'luxuryescapes') {
  return request(`${basePath()}promo/comped-package/${id}?${qs.stringify({ brand })}`, {
    method: 'GET',
  });
}

export async function updateCompedPackage(id, compedData, brand = 'luxuryescapes') {
  return request(`${basePath()}promo/comped-package/${id}?brand=${brand}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(compedData),
  });
}

export async function getBundleByOfferIdRegionAndBrand(offer_id, region, brand) {
  return request(
    `${basePath()}promo/bundle?${qs.stringify({
      offer_id,
      region,
      brand,
    })}`,
    {
      method: 'GET',
    },
  );
}

export async function createOrUpdateBundle(object) {
  return request(`${basePath()}promo/bundle`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(object),
  });
}

export async function deleteBundleById(object) {
  return request(`${basePath()}promo/bundle/`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(object),
  });
}

export async function getPromoUsers(props: GetPromoUserFilters) {
  const queryParameters = {
    page: props.page,
    limit: props.limit,
    code_name: props.code_name ? props.code_name : null,
    user_id: props.user_id ? props.user_id : null,
    id_promo_code: props.id_promo_code ? props.id_promo_code : null,
    brand: props.brand,
    region: props.region,
    expires_after: props.expires_after,
  };

  return request(`${basePath()}promo/users?${qs.stringify(queryParameters, { skipNulls: true })}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function createOrUpdatePromoDiscount(
  discountObject: PromoCodeDiscountMutationPayload,
): Promise<PromoCodeDiscountMutationResponse> {
  const response = await createOrUpdatePromo({
    promo: {
      id_promo_code: discountObject.id_promo_code,
    },
    promoDiscount: discountObject,
    mode: 'create-or-update',
  });

  const promoDiscounts = response.result.discountsUpdated;

  return {
    status: response.status,
    message: response.message,
    result:
      promoDiscounts.length === 1
        ? {
            promoDiscount: promoDiscounts[0],
          }
        : {
            promoDiscounts,
          },
    count: response.count,
    nextPage: response.nextPage,
  };
}

export function deletePromoDiscount(promoId: string, id: string) {
  return request(`${basePath()}promo/id/${promoId}/discount/${id}`, {
    method: 'DELETE',
  });
}

type ReferralLogResponse = {
  config: ReferralConfig;
  result: {
    logs: Array<unknown>;
    total: number;
  };
};

export async function getReferralLogs(
  brand,
  userId,
  logStatus,
  createdAfter,
  orderId,
  pageNum,
  pageSize,
): Promise<ReferralLogResponse> {
  const queryParameters: operations['getReferralLogs']['parameters']['path'] = {
    ...(userId && { user_id: userId }),
    ...(logStatus && logStatus !== 'all' && { referral_log_status: logStatus }),
    ...(createdAfter && { created_before: createdAfter }),
    ...(orderId && { order_id: orderId }),
    page_num: pageNum ?? 1,
    page_size: pageSize ?? 20,
  };

  return request(`${basePath()}referral/logs/debug?${qs.stringify(queryParameters)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

type ReferralLogResetResponse = operations['resetReferralLog']['responses']['200']['content']['application/json'];

export async function resetPromoLog(referralLogId): Promise<ReferralLogResetResponse> {
  return request(`${basePath()}referral/logs/${referralLogId}/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * @deprecated - see usePromo hook
 */
export async function getPromoDiscountSchema() {
  return fetchAndSanitizeSchema(
    `${basePath()}promo/id/241bdbd6-0a4c-4470-8495-4ded391a3728/discount/241bdbd6-0a4c-4470-8495-4ded391a3728`,
  );
}

export type PromoTag = definitions['PromoTag'];
export type PromoUserRole =
  operations['getPromoMeta']['responses']['200']['content']['application/json']['result']['promo_roles_for_user'][number];

/**
 * Avoid using this directly and prefer the usePromo hook
 */
export async function getPromoMeta(
  filters?: operations['getPromoMeta']['responses']['200']['content']['application/json']['result'],
): Promise<operations['getPromoMeta']['responses']['200']['content']['application/json']> {
  return request(`${basePath()}promo/meta?${qs.stringify(filters)}`, {
    method: 'get',
    headers: { 'Content-Type': 'application/json' },
  });
}

export type PromoMigration = {
  id: number | string;
  name: string;
  timestamp: string;
};

export type GetPromoMigrationsResponse = {
  results: {
    schemaVersion: Array<PromoMigration>;
  };
};

export type GetPromoActivitySummaryResponse =
  operations['getPromoActivitySummary']['responses']['200']['content']['application/json'];

export type PromoActivitySummaryTotals = {
  eventCount: number;
  usageCount: number;
  assignmentCount: number;
  byEventType: Record<string, number>;
  recentApplyStats: {
    requestCount: number;
    successfulCount: number;
    unsuccessfulRequestCount: number;
    unsuccessfulLogCount: number;
  };
  friendsAndFamilyStats?: FriendsAndFamilyActivityStats;
  firstOccurredAt?: string;
  lastOccurredAt?: string;
};

export type FriendsAndFamilyActivityStats = {
  requestCount: number;
  acceptedCount: number;
  failedCount: number;
  timedOutCount: number;
  usageCount: number;
  validationFailureCount: number;
};

export type PromoActivitySummary = {
  generatedAt: string;
  expiresAt: string;
  windowStart: string;
  windowEnd: string;
  totals: PromoActivitySummaryTotals;
  promoCodes: Record<string, PromoActivitySummaryTotals>;
  parentPromoCodes: Record<string, PromoActivitySummaryTotals>;
  vouchers: Record<string, PromoActivitySummaryTotals>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function getNumber(record: Record<string, unknown>, key: string): number {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function getOptionalString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

function getString(record: Record<string, unknown>, key: string): string {
  return getOptionalString(record, key) ?? '';
}

function parseFriendsAndFamilyActivityStats(value: unknown): FriendsAndFamilyActivityStats | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  return {
    requestCount: getNumber(value, 'requestCount'),
    acceptedCount: getNumber(value, 'acceptedCount'),
    failedCount: getNumber(value, 'failedCount'),
    timedOutCount: getNumber(value, 'timedOutCount'),
    usageCount: getNumber(value, 'usageCount'),
    validationFailureCount: getNumber(value, 'validationFailureCount'),
  };
}

function parsePromoActivitySummaryTotals(value: unknown): PromoActivitySummaryTotals {
  const record = getRecord(value);
  const recentApplyStats = getRecord(record.recentApplyStats);
  const byEventType = Object.fromEntries(
    Object.entries(getRecord(record.byEventType)).flatMap(([eventType, count]) =>
      typeof count === 'number' && Number.isFinite(count) ? [[eventType, count]] : [],
    ),
  );

  return {
    eventCount: getNumber(record, 'eventCount'),
    usageCount: getNumber(record, 'usageCount'),
    assignmentCount: getNumber(record, 'assignmentCount'),
    byEventType,
    recentApplyStats: {
      requestCount: getNumber(recentApplyStats, 'requestCount'),
      successfulCount: getNumber(recentApplyStats, 'successfulCount'),
      unsuccessfulRequestCount: getNumber(recentApplyStats, 'unsuccessfulRequestCount'),
      unsuccessfulLogCount: getNumber(recentApplyStats, 'unsuccessfulLogCount'),
    },
    friendsAndFamilyStats: parseFriendsAndFamilyActivityStats(record.friendsAndFamilyStats),
    firstOccurredAt: getOptionalString(record, 'firstOccurredAt'),
    lastOccurredAt: getOptionalString(record, 'lastOccurredAt'),
  };
}

function parsePromoActivitySummaryEntities(value: unknown): Record<string, PromoActivitySummaryTotals> {
  return Object.fromEntries(
    Object.entries(getRecord(value)).map(([id, totals]) => [id, parsePromoActivitySummaryTotals(totals)]),
  );
}

/** Narrows the contract's map values, which are published as `unknown`, before the UI reads them. */
export function parsePromoActivitySummary(summary: unknown): PromoActivitySummary {
  const record = getRecord(summary);

  return {
    generatedAt: getString(record, 'generatedAt'),
    expiresAt: getString(record, 'expiresAt'),
    windowStart: getString(record, 'windowStart'),
    windowEnd: getString(record, 'windowEnd'),
    totals: parsePromoActivitySummaryTotals(record.totals),
    promoCodes: parsePromoActivitySummaryEntities(record.promoCodes),
    parentPromoCodes: parsePromoActivitySummaryEntities(record.parentPromoCodes),
    vouchers: parsePromoActivitySummaryEntities(record.vouchers),
  };
}

/** The svc-promo endpoint returns its ten most recently recorded migrations. */
export async function getPromoMigrations({
  signal,
}: { signal?: AbortSignal } = {}): Promise<GetPromoMigrationsResponse> {
  return request(`${basePath()}promo/schema-version`, {
    method: 'GET',
    signal,
  });
}

/** Returns the current Redis activity window; it is intentionally not cached by svc-promo. */
export async function getPromoActivitySummary({
  signal,
}: { signal?: AbortSignal } = {}): Promise<GetPromoActivitySummaryResponse> {
  return request(`${basePath()}promo/activity-summary`, {
    method: 'GET',
    signal,
  });
}

type getPromoTagSummaryProps = {
  filters: operations['getPromoTagSummaries']['parameters']['query'];
};

export async function getPromoTagSummary({
  filters,
}: getPromoTagSummaryProps): Promise<
  operations['getPromoTagSummaries']['responses']['200']['content']['application/json']
> {
  if (filters.dept_tag) {
    delete filters.dept_tag;
  }
  if (filters.category_tag) {
    delete filters.category_tag;
  }

  if (filters.sub_category_tag) {
    delete filters.sub_category_tag;
  }
  return request(`${basePath()}promo/tag/summary?${qs.stringify(filters)}`, {
    method: 'get',
    headers: { 'Content-Type': 'application/json' },
  });
}

type getChannelMarkupByListResponse =
  operations['getChannelMarkupList']['responses']['200']['content']['application/json'];
export async function getChannelMarkupList({
  pageNum,
  pageSize,
  brand,
  status,
}: {
  pageNum: number;
  pageSize: number;
  brand: string;
  status: 'active' | 'expired' | 'all';
}): Promise<getChannelMarkupByListResponse> {
  const queryParams = {
    pageSize,
    page: pageNum,
    brand,
    status,
  };
  const queryString = qs.stringify(queryParams);

  return request(`${basePath()}promo/channel-markup/list?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

type getChannelMarkupListV2Response =
  operations['getChannelMarkupListV2']['responses']['200']['content']['application/json'];
export async function getChannelMarkupListV2(
  {
    page,
    pageSize,
    brand,
    status,
    region,
    currency,
    sortBy,
    sortByDirection,
  }: operations['getChannelMarkupListV2']['parameters']['query'],
  options?: { signal?: AbortSignal },
): Promise<getChannelMarkupListV2Response> {
  const query = qs.stringify(
    {
      page,
      pageSize,
      brand,
      status,
      region,
      currency,
      sortBy,
      sortByDirection,
      version: 'v2',
    },
    { skipNulls: true },
  );

  return request(`${basePath()}promo/channel-markup/v2/list?${query}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

type getClientInitResponse = operations['getClientInit']['responses']['200']['content']['application/json'];
export async function getClientInit(
  query: operations['getClientInit']['parameters']['query'],
  options?: { signal?: AbortSignal },
): Promise<getClientInitResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/client-init?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export const channelMarkupV1 = {
  getChannelMarkupList,
  getChannelMarkupById,
  expireChannelMarkup,
  createChannelMarkup,
  updateChannelMarkup,
} as const;

type getChannelMarkupByIdResponse =
  operations['getChannelMarkupById']['responses']['200']['content']['application/json'];
export async function getChannelMarkupById(channelMarkupId: string): Promise<getChannelMarkupByIdResponse> {
  return request(`${basePath()}promo/channel-markup/${channelMarkupId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export type ChannelMarkupV2Internal = definitions['ChannelMarkupV2Internal'];

type getChannelMarkupV2ByIdResponse = {
  status: 200;
  message: string;
  result: ChannelMarkupV2Internal;
};

export async function getChannelMarkupV2ById(channelMarkupId: string): Promise<getChannelMarkupV2ByIdResponse> {
  return request(`${basePath()}promo/channel-markup/v2/${channelMarkupId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

type deleteChannelMarkupByIdResponse =
  operations['deleteChannelMarkupById']['responses']['200']['content']['application/json'];
export async function expireChannelMarkup(channelMarkupId: string): Promise<deleteChannelMarkupByIdResponse> {
  return request(`${basePath()}promo/channel-markup/${channelMarkupId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

type createChannelMarkupResponse = operations['createChannelMarkup']['responses']['201']['content']['application/json'];
export async function createChannelMarkup(data: {
  source: string;
  medium: string;
  campaign: string;
  term?: string;
  markup?: number;
  luxplus_markup?: number;
  brand: string;
  rules?: Array<{
    region?: string | null;
    condition_type: 'loyaltyOfferType';
    condition_apply_type: 'MUST-INCLUDE' | 'MUST-EXCLUDE';
    condition_value: string;
  }>;
}): Promise<createChannelMarkupResponse> {
  return request(`${basePath()}promo/channel-markup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

type updateChannelMarkupResponse =
  operations['getChannelMarkupById']['responses']['200']['content']['application/json'];
export async function updateChannelMarkup(
  channelMarkupId: string,
  data: {
    source: string;
    medium: string;
    campaign: string;
    term?: string;
    markup?: number;
    luxplus_markup?: number;
    brand: string;
    rules?: Array<{
      region?: string | null;
      condition_type: 'loyaltyOfferType';
      condition_apply_type: 'MUST-INCLUDE' | 'MUST-EXCLUDE';
      condition_value: string;
    }>;
  },
): Promise<updateChannelMarkupResponse> {
  return request(`${basePath()}promo/channel-markup/${channelMarkupId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export type ChannelMarkupV2Condition =
  | components['schemas']['ChannelMarkupV2ConditionLoyaltyOfferType']
  | components['schemas']['ChannelMarkupV2ConditionSubscriberTier']
  | components['schemas']['ChannelMarkupV2ConditionUserRoleCondition'];

type IsEqual<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2
    ? (<T>() => T extends B ? 1 : 2) extends <T>() => T extends A ? 1 : 2
      ? true
      : false
    : false;

type Assert<T extends true> = T;
type _ChannelMarkupV2ConditionMatchesContract = Assert<
  IsEqual<
    ChannelMarkupV2Condition,
    | components['schemas']['ChannelMarkupV2ConditionLoyaltyOfferType']
    | components['schemas']['ChannelMarkupV2ConditionSubscriberTier']
    | components['schemas']['ChannelMarkupV2ConditionUserRoleCondition']
  >
>;

type createOrUpdateChannelMarkupV2Response =
  | operations['createChannelMarkupV2']['responses']['201']['content']['application/json']
  | operations['updateChannelMarkupV2']['responses']['200']['content']['application/json'];
export async function createOrUpdateChannelMarkupV2(
  data:
    | operations['createChannelMarkupV2']['requestBody']['content']['application/json']
    | operations['updateChannelMarkupV2']['requestBody']['content']['application/json'],
  isEditMode: boolean,
): Promise<createOrUpdateChannelMarkupV2Response> {
  return request(`${basePath()}promo/channel-markup/v2`, {
    method: isEditMode ? 'PUT' : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

type checkChannelMarkupV2Response =
  operations['checkChannelMarkupV2']['responses']['200']['content']['application/json'];
export async function checkChannelMarkupV2(
  data: operations['checkChannelMarkupV2']['requestBody']['content']['application/json'],
): Promise<checkChannelMarkupV2Response> {
  return request(`${basePath()}promo/channel-markup/v2/check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

type createChannelMarkupV2Response =
  operations['createChannelMarkupV2']['responses']['201']['content']['application/json'];
export async function createChannelMarkupV2(
  data: operations['createChannelMarkupV2']['requestBody']['content']['application/json'],
): Promise<createChannelMarkupV2Response> {
  return request(`${basePath()}promo/channel-markup/v2`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export const channelMarkupV2 = {
  createChannelMarkupV2,
} as const;

export type GetContentfulFormsResponse =
  operations['getContentfulForms']['responses']['200']['content']['application/json'];
export async function getContentfulForms({
  pageNum,
  pageSize,
  brand,
  slugKeywords,
}: {
  pageNum: number;
  pageSize: number;
  brand: string;
  slugKeywords: string;
}): Promise<GetContentfulFormsResponse> {
  const queryParams = {
    pageSize,
    pageNum,
    slugKeywords,
    brand,
  };
  const queryString = qs.stringify(queryParams);
  return request(`${basePath()}promo/contentful-forms?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export function downloadContentfulFormCsv(slug: string) {
  const downloadUrl = `${basePath()}promo/contentful-forms/${slug}.csv`;
  return request(downloadUrl, {
    download: true,
    method: 'GET',
  });
}

type GetPromoDisplayConfigResponse =
  operations['getPromoDisplayConfigs']['responses']['200']['content']['application/json'];
type GetPromoDisplayConfigParams = operations['getPromoDisplayConfigs']['parameters']['query'];
export async function getPromoDisplayConfigs(
  params: GetPromoDisplayConfigParams,
): Promise<GetPromoDisplayConfigResponse> {
  const nonEmptyParams = filterOutEmptyFields(params);
  // Adding a random tick to the query string to prevent caching
  const queryString = qs.stringify({ ...nonEmptyParams, tick: Date.now() });

  return request(`${basePath()}promo/promo-display-config-internal?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

type createPromoDisplayConfigPayload =
  operations['createPromoDisplayConfig']['requestBody']['content']['application/json'];
type createPromoDisplayConfigResponse =
  operations['createPromoDisplayConfig']['responses']['201']['content']['application/json'];
export async function createPromoDisplayConfig(
  data: createPromoDisplayConfigPayload,
): Promise<createPromoDisplayConfigResponse> {
  return request(`${basePath()}promo/promo-display-config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filterOutEmptyFields(data)),
    ignoreResponse: true,
  });
}

type expirePromoDisplayConfigResponse =
  operations['expirePromoDisplayConfig']['responses']['200']['content']['application/json'];
export async function expirePromoDisplayConfig(id: string): Promise<expirePromoDisplayConfigResponse> {
  return request(`${basePath()}promo/promo-display-config/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

type updatePromoDisplayConfigPayload =
  operations['updatePromoDisplayConfig']['requestBody']['content']['application/json'];
type updatePromoDisplayConfigResponse =
  operations['updatePromoDisplayConfig']['responses']['200']['content']['application/json'];
export async function updatePromoDisplayConfig(
  data: updatePromoDisplayConfigPayload,
): Promise<updatePromoDisplayConfigResponse> {
  return request(`${basePath()}promo/promo-display-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filterOutEmptyFields(data)),
  });
}

export type CheckPromoDisplayOfferParams = {
  offerId: string;
  promoDisplayConfigId: string;
  brand?: string;
  sessionId?: string;
};

export type CheckPromoDisplayOfferResponse = {
  status: 200;
  message: string;
  result: {
    check_passed?: boolean;
    checkPassed: boolean;
    codeName?: string;
    promoDisplayOptionId?: string;
    displayOptionKey?: string;
    resultCode?: string;
  };
};

type createOrUpdatePromoDisplayConfigRuleJsonPayload = {
  rule_json: operations['createOrUpdatePromoDisplayConfigRuleJson']['requestBody']['content']['application/json']['rule_json'];
};
type createOrUpdatePromoDisplayConfigRuleJsonResponse =
  operations['createOrUpdatePromoDisplayConfigRuleJson']['responses']['200']['content']['application/json'];

export async function createOrUpdatePromoDisplayConfigRuleJson(
  promoDisplayConfigId: string,
  data: createOrUpdatePromoDisplayConfigRuleJsonPayload,
): Promise<createOrUpdatePromoDisplayConfigRuleJsonResponse> {
  return request(`${basePath()}promo/promo-display-config/${promoDisplayConfigId}/rules`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function checkPromoDisplayOffer(
  params: CheckPromoDisplayOfferParams,
): Promise<CheckPromoDisplayOfferResponse> {
  const queryParams: operations['checkPromoDisplayOffer']['parameters']['query'] = {
    brand: params.brand || 'luxuryescapes',
    skipSave: 'true',
    ...(params.sessionId && { sessionId: params.sessionId }),
  };

  const queryString = qs.stringify(queryParams);

  return request(
    `${basePath()}promo/promo-display-config/${params.promoDisplayConfigId}/check-offer/${
      params.offerId
    }?${queryString}`,
    {
      method: 'GET',
    },
  );
}

type getReferralEarnOptionQueryParams = operations['getReferralEarnOptionList']['parameters']['query'];
export type getReferalEarnOptionResponse =
  operations['getReferralEarnOptionList']['responses']['200']['content']['application/json'];
export async function getReferralEarnOptions(
  payload: getReferralEarnOptionQueryParams,
): Promise<getReferalEarnOptionResponse> {
  const queryString = qs.stringify(payload);
  return request(`${basePath()}referral/earn-options/list?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export type createReferralEarnOptionPayload =
  operations['createReferralEarnConfig']['requestBody']['content']['application/json'];
type createReferralEarnOptionResponse =
  operations['createReferralEarnConfig']['responses']['201']['content']['application/json'];
export async function createReferralEarnOption(
  payload: createReferralEarnOptionPayload,
): Promise<createReferralEarnOptionResponse> {
  return request(`${basePath()}referral/earn-options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function expireReferralEarnOption(id: string) {
  return request(`${basePath()}referral/earn-options/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export type updateReferralEarnOptionPayload =
  operations['updateReferralEarnOption']['requestBody']['content']['application/json'];
type updateReferralEarnOptionResponse =
  operations['updateReferralEarnOption']['responses']['200']['content']['application/json'];
export async function updateReferralEarnOption(
  payload: updateReferralEarnOptionPayload,
): Promise<updateReferralEarnOptionResponse> {
  return request(`${basePath()}referral/earn-options`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export const getPromoDisplayCheckResults = async (
  filter: GetPromoDisplayCheckResultsFilter,
): Promise<PromoDisplayCheckResultsResponse> => {
  return request(`${basePath()}promo/promo-display-config-results?${qs.stringify(filter)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

// Voucher Display Config
export type GetVoucherDisplayConfigsParams = operations['getVoucherDisplayConfigsInternal']['parameters']['query'];
export type CreateVoucherDisplayConfigBody =
  operations['createVoucherDisplayConfig']['requestBody']['content']['application/json'];
export type UpdateVoucherDisplayConfigBody =
  operations['updateVoucherDisplayConfig']['requestBody']['content']['application/json'];
export type VoucherDisplayConfigResult =
  operations['getVoucherDisplayConfig']['responses']['200']['content']['application/json'];

export async function getVoucherDisplayConfigs(params: GetVoucherDisplayConfigsParams): Promise<any> {
  const nonEmptyParams = filterOutEmptyFields(params);
  const queryString = qs.stringify(nonEmptyParams);
  return request(`${basePath()}promo/voucher-display-config-internal?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getVoucherDisplayConfig(id: string): Promise<VoucherDisplayConfigResult> {
  return request(`${basePath()}promo/voucher-display-config/${id}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function createVoucherDisplayConfig(data: CreateVoucherDisplayConfigBody): Promise<any> {
  return request(`${basePath()}promo/voucher-display-config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filterOutEmptyFields(data)),
  });
}

export async function updateVoucherDisplayConfig(data: UpdateVoucherDisplayConfigBody): Promise<any> {
  return request(`${basePath()}promo/voucher-display-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filterOutEmptyFields(data)),
  });
}

export async function expireVoucherDisplayConfig(id: string): Promise<any> {
  return request(`${basePath()}promo/voucher-display-config/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export const displayConditionType = (conditionType: string) => {
  switch (conditionType) {
    case 'gt':
      return '>';
    case 'lt':
      return '<';
    case 'eq':
      return '=';
    case 'gte':
      return '>=';
    case 'lte':
      return '<=';
    case 'ne':
      return '!=';
    default:
      return conditionType;
  }
};

export type CreateOrUpdatePromoPayload =
  operations['createOrUpdatePromoCode']['requestBody']['content']['application/json'];
export type CreateOrUpdatePromoResult =
  operations['createOrUpdatePromoCode']['responses']['200']['content']['application/json']['result'];
export type CreateOrUpdatePromoResponse =
  operations['createOrUpdatePromoCode']['responses']['200']['content']['application/json'];
export type CheckPromoQuery = operations['checkPromoCode']['parameters']['query'];
export type CheckPromoResponse = operations['checkPromoCode']['responses']['200']['content']['application/json'];
export type CheckPromoResult =
  operations['checkPromoCode']['responses']['200']['content']['application/json']['result'];

type DisplayField = 'productType' | 'originAirportCode' | 'destinationAirportCode';
type DiscountRequestItemKeys = keyof definitions['Discount Request']['order']['items']['0'];
/** placeId / lePlaceId are intentionally excluded from promo item UI (lat/lng only for geo). */
type ExpectedFields = Record<Exclude<DiscountRequestItemKeys, 'placeId' | 'lePlaceId'> | DisplayField, boolean>;
type CategoryBK = definitions['Discount Request']['order']['items']['0']['categoryBK'];
type SubCategoryBK = definitions['Discount Request']['order']['items']['0']['subCategoryBK'];

/**
 * If the new field is always expected to be present on the promo request item, add it to the `defaultExpectedFields` with a value of true. If it's only expected for specific product types, add it to the relevant product type and subcategory configurations in `productTypesExpectedFields`.
 */
const defaultExpectedFields: ExpectedFields = {
  productType: true,
  discountableTotal: true,
  offerId: true,
  itemId: true,
  categoryBK: true,
  subCategoryBK: true,
  transactionKey: true,
  loyaltyOfferType: true,
  fareType: false,
  itemModifierInfo: false,
  provider: true,
  luxPlusPrice: false,
  numberOfNights: false,
  numberOfAdults: false,
  numberOfChildren: false,
  originAirportCode: false,
  destinationAirportCode: false,
  itemCountryCode: false,
  travellers: false,
  itemInfoString: false,
  reservationType: false,
  isMemberPrice: false,
  mx: false,
  itemTypeSpecificId: false,
  isFnFBooking: true,
  cartItemId: false,
  startDate: true,
  endDate: true,
  itemLat: false,
  itemLng: false,
  hotelInfo: false,
  flightInfo: false,
  discountResult: false,
};
/***
 * When the field is not expected in a specific product type, configure it to false below to suppress warnings
 * on the promo request items display.
 * startDate / endDate default to expected; insurance, gift_card, credits, and experience set them false
 * (no stay window on item).
 */
export const productTypesExpectedFields: Record<
  CategoryBK,
  Partial<Record<Exclude<SubCategoryBK, undefined> | '*', ExpectedFields>> & { '*': ExpectedFields }
> = {
  ALL: {
    '*': defaultExpectedFields,
  },
  flight: {
    '*': {
      ...defaultExpectedFields,
      flightInfo: true,
      originAirportCode: true,
      itemCountryCode: true,
      fareType: true,
      mx: true,
      destinationAirportCode: true,
      provider: true,
      subCategoryBK: false,
    },
  },
  transfer: {
    '*': {
      ...defaultExpectedFields,
      mx: true,
      subCategoryBK: false,
    },
  },
  addon: {
    '*': defaultExpectedFields,
  },
  subscription: {
    '*': {
      ...defaultExpectedFields,
      provider: false,
      offerId: false,
      mx: false,
      startDate: false,
      endDate: false,
    },
  },
  car_hire: {
    '*': defaultExpectedFields,
  },
  experience: {
    '*': {
      ...defaultExpectedFields,
      mx: true,
      provider: false,
      startDate: false,
    },
  },
  tour: {
    '*': {
      ...defaultExpectedFields,
      provider: true,
      itemCountryCode: true,
      itemId: false,
      subCategoryBK: false,
      itemLat: true,
      itemLng: true,
      numberOfNights: true,
      numberOfAdults: true,
      numberOfChildren: true,
    },
  },
  hotel: {
    '*': {
      ...defaultExpectedFields,
      reservationType: true,
      isMemberPrice: true,
      discountableTotal: true,
      travellers: true,
      itemCountryCode: true,
      itemLat: true,
      itemLng: true,
      numberOfNights: true,
      numberOfAdults: true,
      numberOfChildren: true,
      hotelInfo: true,
    },
  },
  cruise: {
    '*': {
      ...defaultExpectedFields,
      itemCountryCode: true,
      itemLat: false,
      itemLng: false,
      numberOfNights: true,
      numberOfAdults: true,
      numberOfChildren: true,
    },
  },
  service_fee: {
    '*': {
      ...defaultExpectedFields,
      itemCountryCode: false,
      subCategoryBK: false,
      reservationType: false,
      provider: false,
      startDate: false,
      endDate: false,
    },
  },
  insurance: {
    '*': {
      ...defaultExpectedFields,
      itemCountryCode: false,
      provider: false,
      offerId: false,
      subCategoryBK: false,
      startDate: false,
      endDate: false,
    },
  },
  shop: {
    '*': {
      ...defaultExpectedFields,
      offerId: false,
    },
    'shop-shop_shipping': {
      ...defaultExpectedFields,
      offerId: false,
      mx: false,
    },
  },
  gift_card: {
    '*': {
      ...defaultExpectedFields,
      offerId: false,
      mx: false,
      startDate: false,
      endDate: false,
    },
  },
  credits: {
    '*': {
      ...defaultExpectedFields,
      offerId: false,
      mx: false,
      startDate: false,
      endDate: false,
    },
  },
  loyalty_points: {
    '*': {
      ...defaultExpectedFields,
      offerId: false,
      mx: false,
      startDate: false,
      endDate: false,
    },
  },
};

export type CreatePromoTagPayload = operations['createPromoTag']['requestBody']['content']['application/json'];

export type GetPromoTagsParams = operations['getPromoTags']['parameters']['query'];
export type GetPromoTagResponse = operations['getPromoTags']['responses']['200']['content']['application/json'];

export type UpdatePromoTagPayload = operations['updatePromoTag']['requestBody']['content']['application/json'];

export async function getPromoTags(query: GetPromoTagsParams): Promise<GetPromoTagResponse> {
  const queryString = QueryString.stringify(query);
  return request(`${basePath()}promo/tags?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

type BuildTagIdProps =
  | {
      id: string;
    }
  | {
      departmentTag: string;
      categoryTag: string;
      subCategoryTag: string;
    };

export const buildTagId = (tag: BuildTagIdProps): string => {
  if ('id' in tag) {
    return tag.id;
  }
  return `${tag.departmentTag}_${tag.categoryTag}_${tag.subCategoryTag}`;
};

export async function createPromoTag(payload: CreatePromoTagPayload): Promise<{
  status: number;
  result: PromoTag;
}> {
  return request(`${basePath()}promo/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function updatePromoTag(payload: UpdatePromoTagPayload): Promise<{
  status: number;
  result: PromoTag;
}> {
  return request(`${basePath()}promo/tags`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// Offer Metrics Types and Functions
export type GetPromoOfferMetricsResponse =
  operations['getOfferMetrics']['responses']['200']['content']['application/json'];

export type PromoOfferMetric =
  operations['getOfferMetrics']['responses']['200']['content']['application/json']['result']['offerMetrics']['0'];

export type FetchOfferMetricsParams = operations['getOfferMetrics']['parameters']['query'];

export type AddMockOfferMetricsDataParams =
  operations['mockOfferMetrics']['requestBody']['content']['application/json'];

export type AddMockOfferMetricsDataResponse =
  operations['mockOfferMetrics']['responses']['200']['content']['application/json'];

export type SyncPromoOfferMetricsParams = operations['syncOfferMetrics']['requestBody']['content']['application/json'];
export type SyncPromoOfferMetricsResponse =
  operations['syncOfferMetrics']['responses']['200']['content']['application/json'];

export async function getPromoOfferMetrics(
  offerId: string,
  page: number,
  region: string,
  brand: string,
  limit: number,
  sortBy?: operations['getOfferMetrics']['parameters']['query']['sortBy'],
  sortOrder?: operations['getOfferMetrics']['parameters']['query']['sortOrder'],
): Promise<GetPromoOfferMetricsResponse> {
  const params: operations['getOfferMetrics']['parameters']['query'] = {
    ...(offerId && { offerId }),
    ...(page && { page: page.toString() }),
    ...(region && { region }),
    ...(brand && { brand }),
    ...(limit && { limit: limit.toString() }),
    ...(sortBy && { sortBy }),
    ...(sortOrder && { sortOrder }),
  };

  const queryString = qs.stringify(params);
  return request(`${basePath()}/promo/offer-metrics?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function addMockOfferMetricsData(params: AddMockOfferMetricsDataParams): Promise<any> {
  const mockBody: operations['mockOfferMetrics']['requestBody']['content']['application/json'] = {
    mockOfferMetrics: params.mockOfferMetrics,
  };
  return request(`${basePath()}/promo/offer-metrics/mock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mockBody),
  });
}

export async function syncPromoOfferMetrics(
  params: SyncPromoOfferMetricsParams,
): Promise<SyncPromoOfferMetricsResponse> {
  return request(`${basePath()}/promo/offer-metrics/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export type GetRolesForUserResponse = {
  status: number;
  result: {
    roles: Array<PromoRole>;
  };
};

export async function getRolesForUser(userId: string): Promise<GetRolesForUserResponse> {
  return request(`${basePath()}promo/roles/user/${userId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export type HealPromoParams = {
  targetPromoCodeName: string;
  brand?: string;
  page?: number;
  limit?: number;
};

export type HealPromoResponse = {
  status: number;
  message: string;
  result: {
    healedPromo?: any;
    totalRemapped?: number;
  };
};

export async function healPromo(params: HealPromoParams): Promise<HealPromoResponse> {
  const { targetPromoCodeName, brand, page, limit } = params;

  const queryParams: Record<string, string> = {};
  if (brand) {
    queryParams.brand = brand;
  }
  if (page) {
    queryParams.page = page.toString();
  }
  if (limit) {
    queryParams.limit = limit.toString();
  }

  const queryString = qs.stringify(queryParams);
  const url = `${basePath()}promo/clone-heal/${targetPromoCodeName}${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Sorts allowed payment methods with deprecated ones at the bottom, then alphabetically
 * @param paymentMethods Array of payment method objects
 * @returns Sorted array of payment methods
 */
export function sortAllowedPaymentMethods(paymentMethods: PromoMeta['promo_allowed_payment_methods']) {
  return [...paymentMethods].sort((a, b) => {
    // First sort by deprecated status (non-deprecated first)
    if (a.deprecated !== b.deprecated) {
      return Number(a.deprecated) - Number(b.deprecated);
    }

    // Then sort alphabetically by method name
    return a.method.localeCompare(b.method);
  });
}

// Loyalty Service

export type GetLoyaltyPromoKeyOptionsResponse =
  operations['getLoyaltyPromoKeyOptions']['responses']['200']['content']['application/json'];

export type GetLoyaltyPromoKeyOptionsParams = operations['getLoyaltyPromoKeyOptions']['parameters']['query'];

export type LoyaltyPromoKeyOption = {
  brand: string;
  region: string;
  promoKey: string;
  customRuleId: string;
  createdBy: string;
  createdAt: string;
  loyaltyCustomRule: {
    id: string;
    createdAt: string;
    createdBy: string;
    type: 'BONUS_POINTS';
    purpose: 'ESTIMATION_ONLY';
    pointsType: 'BASE_POINTS';
    productType: string;
    name: string;
    offerId: string;
    identifierDescription: string;
    packageId: string;
    earnKey: string;
    promoKey: string;
    region: string;
    pointsCap: number;
    pointsValue: number;
    pointsRatio: number;
    pointsMultiplier: number;
    pointsValidDays: number;
    pointsValidMonths: number;
    metaData: Record<string, any>;
    startDate: string;
    endDate: string;
  };
};

export type CreateLoyaltyPromoKeyOptionPayload = {
  brands: Array<string>;
  regions: Array<string>;
  promoKey: string;
  loyaltyCustomRulePayload: {
    type: 'BONUS_POINTS';
    purpose: 'ESTIMATION_ONLY';
    pointsType: 'BASE_POINTS';
    productType: string;
    name: string;
    offerId?: string;
    identifierDescription?: string;
    packageId?: string;
    earnKey: string;
    promoKey: string;
    region: string;
    pointsCap?: number;
    pointsValue?: number;
    pointsRatio?: number;
    pointsMultiplier?: number;
    pointsValidDays?: number;
    pointsValidMonths?: number;
    metaData?: Record<string, any>;
    startDate?: string;
    endDate?: string;
  };
};

export async function getLoyaltyPromoKeyOptions(
  getLoyaltyPromoKeyOptionsParams: GetLoyaltyPromoKeyOptionsParams,
): Promise<GetLoyaltyPromoKeyOptionsResponse> {
  const queryString = qs.stringify(getLoyaltyPromoKeyOptionsParams, { skipNulls: true });
  const url = queryString
    ? `${basePath()}promo/loyalty-promo-key-options?${queryString}`
    : `${basePath()}promo/loyalty-promo-key-options`;
  return request(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

// Marketing router rules
export type MarketingRouterQuery = operations['getMarketingRouter']['parameters']['query'];
export type MarketingRouterResponse =
  operations['getMarketingRouter']['responses']['200']['content']['application/json'];
export type MarketingRouterInternalQuery = operations['getMarketingRouterInternal']['parameters']['query'];
export type MarketingRouterInternalResponse =
  operations['getMarketingRouterInternal']['responses']['200']['content']['application/json'];
export type MarketingRouterCreatePayload =
  operations['createMarketingRouter']['requestBody']['content']['application/json'];
export type MarketingRouterCreateResponse =
  operations['createMarketingRouter']['responses']['201']['content']['application/json'];
export type MarketingRouterUpdatePayload =
  operations['updateMarketingRouter']['requestBody']['content']['application/json'];
export type MarketingRouterUpdateResponse =
  operations['updateMarketingRouter']['responses']['200']['content']['application/json'];

export async function listMarketingRouters(
  query: MarketingRouterQuery,
  options?: { signal?: AbortSignal },
): Promise<MarketingRouterResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/marketing-router?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function listMarketingRouterInternal(
  query: MarketingRouterInternalQuery,
  options?: { signal?: AbortSignal },
): Promise<MarketingRouterInternalResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath()}promo/marketing-router/internal?${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function createMarketingRouter(
  payload: MarketingRouterCreatePayload,
): Promise<MarketingRouterCreateResponse> {
  return request(`${basePath()}promo/marketing-router`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function updateMarketingRouter(
  payload: MarketingRouterUpdatePayload,
): Promise<MarketingRouterUpdateResponse> {
  return request(`${basePath()}promo/marketing-router`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export type MarketingRouterSignificanceQuery = operations['getMarketingRouterSignificance']['parameters']['query'];
export type MarketingRouterSignificanceResponse =
  operations['getMarketingRouterSignificance']['responses']['200']['content']['application/json'];

export type MarketingRouterSignificanceTimeseriesResponse =
  operations['getMarketingRouterSignificanceTimeseries']['responses']['200']['content']['application/json'];

export async function getMarketingRouterSignificanceTimeseries(
  marketingRouterId: string,
  query: MarketingRouterSignificanceQuery,
  options?: { signal?: AbortSignal },
): Promise<MarketingRouterSignificanceTimeseriesResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  const base = `${basePath()}promo/marketing-router/${encodeURIComponent(marketingRouterId)}/significance/timeseries`;
  return request(queryString ? `${base}?${queryString}` : base, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}

export async function getMarketingRouterSignificance(
  marketingRouterId: string,
  query: MarketingRouterSignificanceQuery,
  options?: { signal?: AbortSignal },
): Promise<MarketingRouterSignificanceResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  const base = `${basePath()}promo/marketing-router/${encodeURIComponent(marketingRouterId)}/significance`;
  return request(queryString ? `${base}?${queryString}` : base, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: options?.signal,
  });
}
